import {mountProductEngagement} from './core/product-engagement';
import {markCampaignOffer,refreshCampaignBadges} from "./core/campaign-badge";
import {dockedSearchTarget} from './core/docked-search';
import { EventDelivery } from "./core/event-delivery";
import { OutfitPanel } from './core/outfit-panel';
import { connectMerchantProCommerce } from "./core/merchantpro-commerce";
import { storefrontProfile } from './core/storefront-profile';
import {mountRecommendationFlows} from './core/recommendation-flows';
import {currentRecommendationClick} from './core/recommendation-attribution';
import { checkInstallation } from './core/installation-check';
import { mountVoiceSearch } from './core/voice-search';
import {mountQuiz,registerCategoryMerchandising,mountCategoryMerchandising} from './core/discovery-tools';
import { relatedSearch, hasSpecificConstruction, hasExplicitSizeRequest, hasSeasonRequest } from './core/related-search';
import { trackingPayload } from './core/tracking-payload';
import { emitAssistantAnalytics } from './core/assistant-analytics';
import { mountShoppingAssistant } from './core/shopping-assistant';
import { STYLES } from "./core/styles";
import { productBadge } from "./core/product-badge";
import { productFamilyKey } from './core/product-family';
import { sidebarRecommendationItems } from './core/sidebar-recommendations';
import { LIVE_SEARCH_DELAY_MS, SUGGEST_DEBOUNCE_MS } from "./core/timing";
import { translations, copyFor } from "./core/i18n";
import { analyticsMode, SearchAnalytics } from "./core/analytics";
import type {
  WidgetConfig,
  SearchResult,
  SearchResponse,
  DiscoverTerm,
  DiscoverChip,
  DiscoverRecommendation,
  DiscoverResponse,
  DiscoverIntent,
  FacetValue,
  SuggestionsResponse,
  VisualSearchResponse,
} from "./core/types";
import { DISCOVER_TTL_MS } from "./core/types";
import { ALL_MODULES_ENABLED, loadModuleFlags, type ModuleFlags } from "./core/modules";
import { imageFromClipboard, pasteAimedAtPageField } from "./core/clipboard";
import { discoveryTypeChips } from "./core/discovery-chips";
import { mountCompactRefinement } from "./core/compact-refinement";
import { buildSiteSearchUrl } from "./core/site-search-url";

interface AttributionState {
  assistantConversationId?: string;
  queryId?: string;
  productId?: string;
  productName?: string;
  productUrl?: string;
  imageUrl?: string;
  at: number;
}

interface CartRecoveryState extends AttributionState {
  schemaVersion: 7;
  addedAt: number;
  quantity: number;
  regularPrice?: number;
  salePrice?: number;
  currency?: string;
  lastShownAt?: number;
  shownCount?: number;
  dismissedUntil?: number;
  snoozedUntil?: number;
}

interface CartRecoveryOffer {
  enabled: boolean;
  delayMinutes: number;
  couponEnabled: boolean;
  couponCode: string | null;
  couponPercent: number | null;
  minimumSubtotal: number | null;
  headline: string;
  message: string;
}

const DEFAULT_CART_RECOVERY_DELAY_MINUTES = 2;

class SmartyFindElement extends HTMLElement {
  private disposeVoice?: () => void;
  private disposeProductEngagement?: () => void;
  private disposeRecommendations?: () => void;
  private assistant?: ReturnType<typeof mountShoppingAssistant>;
  private config!: WidgetConfig;
  private root!: ShadowRoot;
  private debounceTimer?: number;
  private cartRecoveryTimer?: number;
  private cartRecoveryValidationPending = false;
  private cartRecoveryState: CartRecoveryState | null = null;
  private cartRecoveryOfferLoaded = false;
  private cartRecoveryOffer: CartRecoveryOffer = {
    enabled: true,
    delayMinutes: DEFAULT_CART_RECOVERY_DELAY_MINUTES,
    couponEnabled: false,
    couponCode: null,
    couponPercent: null,
    minimumSubtotal: null,
    headline: "Ai lăsat ceva în coș",
    message: "Produsul ales este încă în coș. Revino înainte să se epuizeze mărimea.",
  };
  private activeRequest?: AbortController;
  private visualSearchActive = false;
  private outfitPanel?: OutfitPanel;
  private sessionId = "";
  private activeQueryId?: string;
  private sidebarResults: SearchResult[] = [];
  private sidebarQueryId = "";
  private discoverCache = new Map<string, { at: number; data: DiscoverResponse }>();
  private discoverIntent: DiscoverIntent = "default";
  private discoverLoading = false;
  private discoveryViewTracked = false;
  private pendingDiscoverIntent?: DiscoverIntent;
  private discoverImages: HTMLImageElement[] = [];
  private discoverRequest?: AbortController;
  private hijackInputs: HTMLInputElement[] = [];
  private hijackObserver?: MutationObserver;
  private activeInput?: HTMLInputElement;
  private desktopReturnInput?: HTMLInputElement;
  private previousBodyOverflow?: string;
  private restoringSearchFocus = false;
  /** Modulele active, aduse o dată la încărcare (vezi core/modules.ts). */
  private flags: ModuleFlags = ALL_MODULES_ENABLED;
  /** Setat cand pornim noi navigarea, ca linkul sa nu mai numere inca o data clicul. */
  private navigating = false;
  private repositionFrame = 0;
  /** Gestul de glisare a panoului în jos, pe mobil. */
  private dragStartY = 0;
  private dragging = false;
  /**
   * Paginarea rezultatelor: API-ul întoarce primele `limit` rezultate, iar `totalResults`
   * este exact câte a întors, nu câte potriviri are catalogul. „Mai multe" înseamnă deci
   * o cerere nouă cu limită mai mare, care păstrează ordinea și adaugă ce urmează.
   */
  private resultQuery = "";
  private resultLimit = 0;
  private resultCount = 0;
  private loadingMore = false;
  private moreRequest?: AbortController;
  private searchEpoch = 0;
  private panelScrollFrame = 0;
  private panelObserver?: MutationObserver;
  private readonly maxResultLimit = 100;
  /** Trimite căutările către GA4 / Meta Pixel / TikTok, dacă site-ul le are. */
  private analytics?: SearchAnalytics;
  private commerceDelivery?: EventDelivery;
  /** Sugestii la tastare (modulul „anticipation"): apar până vin rezultatele. */
  private suggestTimer = 0;
  /**
   * Câte un observator pe fiecare rând de chip-uri. Unul singur, refolosit, ar deconecta
   * rândul de nivel 1 când se cheamă pentru nivelul 2 — adică exact estomparea de margine
   * care arată că mai există chip-uri. Cheia e elementul, ca fiecare rând să-și țină starea.
   */
  private readonly chipObservers = new Map<HTMLElement, ResizeObserver>();
  private refinementSync?: (query: string) => void;
  /** Ascultătorul de derulare al fiecărui rând, ca să nu se adune la fiecare randare. */
  private readonly chipScrollListeners = new Map<HTMLElement, () => void>();
  private suggestRequest?: AbortController;
  /** Urmărește degetul, ca o derulare să nu fie luată drept apăsare pe produs. */
  private touchOrigin?: { x: number; y: number };
  private suppressClickUntil = 0;
  private readonly closeOnOutsidePointer = (event: Event): void => {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (path.includes(this) || (this.root && path.includes(this.root))) return;
    const target = event.target as Node | null;
    if (target && (this.contains(target) || (this.root && (this.root as unknown as Node).contains(target)))) return;
    // În modul hijack, inputurile site-ului sunt în afara shadow DOM-ului.
    if (this.hijackInputs.some((input) => path.includes(input) || (target && (input === target || input.contains(target))))) return;
    this.hideOutput();
    this.deactivateMobileSearch();
  };
  /**
   * „Print screen" + Ctrl+V: o captură de ecran ajunge direct în căutarea după
   * poză, fără să fie salvată într-un fișier. Textul lipit rămâne al browserului,
   * iar câmpurile site-ului (formular, cupon, chat) nu sunt atinse.
   */
  private readonly onPaste = (event: ClipboardEvent): void => {
    if (!this.flags.enabled("visual_search")) return;
    const path = typeof event.composedPath === "function" ? event.composedPath() : [];
    const insideWidget = path.includes(this.root) || path.includes(this);
    if (!insideWidget && pasteAimedAtPageField(path)) return;
    const file = imageFromClipboard(event.clipboardData);
    if (!file) return;
    event.preventDefault();
    void this.runVisualSearchFile(file);
  };

  private scrollInputToTop(input: HTMLInputElement): void {
    // The fixed SmartyFind toolbar does not follow the store's hidden drawer.
    if (this.dataset.openSelector) return;
    if (!window.matchMedia('(max-width: 620px), (orientation: landscape) and (max-height: 500px) and (max-width: 1200px)').matches) return;
    const form = input.closest('form');
    const targetEl = form ?? input;
    const rect = targetEl.getBoundingClientRect();
    if (rect.top > 8 || rect.top < 0) {
      const currentY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
      const targetY = Math.max(0, currentY + rect.top - 6);
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }
    // Mentine panoul sincronizat si lipit de bara de cautare pe durata scroll-ului
    const startTime = performance.now();
    const track = (now: number) => {
      this.anchorTo(input);
      if (now - startTime < 450) {
        requestAnimationFrame(track);
      } else {
        this.anchorTo(input);
      }
    };
    requestAnimationFrame(track);
  }
  private externalSearchTrigger?: HTMLElement;
  private readonly onExternalSearchOpen = (event: Event): void => {
    const selector = this.dataset.openSelector;
    if (!selector || !(event.target instanceof Element)) return;
    let trigger: Element | null = null;
    try { trigger = event.target.closest(selector); } catch { return; }
    if (!(trigger instanceof HTMLElement)) return;
    const input = this.hijackInputs.find(item => item.isConnected);
    if (!input) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.externalSearchTrigger = trigger;
    this.onHijackFocus({ target: input } as unknown as Event);
    const toolbar = this.root.querySelector<HTMLInputElement>('input[type="search"]');
    if (toolbar) { toolbar.value = input.value; toolbar.getBoundingClientRect(); toolbar.focus(); }
  };

  private resolveHijackInput(source:HTMLInputElement):HTMLInputElement {
    const target=dockedSearchTarget(source,this.hijackInputs);
    if(target===source)return source;
    target.value=source.value;
    source.closest('.storefront-handheld-footer-bar .search')?.classList.remove('active');
    target.scrollIntoView({block:'start',behavior:'instant'});
    this.restoringSearchFocus=true;
    try { target.focus({preventScroll:true}); } finally { this.restoringSearchFocus=false; }
    return target;
  }
  private readonly onFooterSearchOpen=(event:Event):void=>{
    if(!(event.target instanceof Element))return;
    const trigger=event.target.closest('.storefront-handheld-footer-bar .search > a');
    if(!trigger)return;
    const source=trigger.parentElement?.querySelector<HTMLInputElement>('input[type="search"]');
    if(!source||!this.hijackInputs.includes(source))return;
    const target=this.resolveHijackInput(source);
    if(target===source)return;
    event.preventDefault();event.stopImmediatePropagation();
    this.onHijackFocus({target} as unknown as Event);
  };

  private readonly onHijackFocus = (event: Event): void => {
    if (this.restoringSearchFocus) return;
    const input = this.resolveHijackInput(event.target as HTMLInputElement);
    this.activeInput = input;
    this.activateMobileSearch();
    this.scrollInputToTop(input);
    this.anchorTo(input);
    const value = input.value.trim();
    if (value.length < 2) {
      this.showDiscover();
    }
    // Pe paginile unde bara are deja un termen (ex. rezultatele căutării),
    // click-ul redeschide rezultatele pentru acel termen.
    if (value.length >= 2 && this.root.querySelector<HTMLElement>(".sf-output")?.hidden) void this.search(value, true);
    // Native focus can reveal the overlay before click is dispatched. Move focus now,
    // rather than leaving the keyboard attached to the obscured storefront field.
    if (this.dataset.openSelector && this.classList.contains('sf-mobile-active')) {
      const toolbar = this.root.querySelector<HTMLInputElement>('input[type="search"]');
      if (toolbar) {
        toolbar.value = input.value;
        toolbar.getBoundingClientRect();
        this.restoringSearchFocus = true;
        try { toolbar.focus(); } finally { this.restoringSearchFocus = false; }
        this.activeInput = toolbar;
      }
    }
  };
  private readonly onHijackInput = (event: Event): void => {
    this.visualSearchActive = false;
    const input = this.resolveHijackInput(event.target as HTMLInputElement);
    this.activeInput = input;
    this.activateMobileSearch();
    this.anchorTo(input);
    if (this.debounceTimer) window.clearTimeout(this.debounceTimer);
    const query = input.value.trim();
    if (query.length < 2) {
      this.showDiscover();
      return;
    }
    this.scheduleSuggestions(input, query);
    this.debounceTimer = window.setTimeout(() => void this.search(query, true), LIVE_SEARCH_DELAY_MS);
  };
  private readonly onHijackSubmit = (event: Event): void => {
    const form = event.currentTarget as HTMLFormElement;
    const source = this.hijackInputs.find((item) => item.form === form);
    if (!source || source.value.trim().length < 2) return;
    const input=this.resolveHijackInput(source);
    event.preventDefault();
    this.activeInput = input;
    if (this.debounceTimer) window.clearTimeout(this.debounceTimer);
    this.activateMobileSearch();
    this.anchorTo(input);
    void this.search(input.value.trim(), true);
  };
  private readonly onHijackKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") this.hideOutput();
    if (event.key === "ArrowDown" && this.focusPanelItem(1)) event.preventDefault();
  };
  private readonly reposition = (): void => {
    if (!this.activeInput) return;
    if (this.root.querySelector<HTMLElement>(".sf-output")?.hidden) return;
    if (this.repositionFrame) return;
    this.repositionFrame = window.requestAnimationFrame(() => {
      this.repositionFrame = 0;
      if (this.activeInput) this.anchorTo(this.activeInput);
    });
  };
  /**
   * Cat loc vizibil are panoul sub bara de cautare. Cand apare tastatura,
   * viewport-ul vizual scade, deci panoul se opreste deasupra ei.
   */
  private updatePanelBounds(): void {
    if (!this.activeInput) return;
    if (!window.matchMedia("(max-width: 620px), (orientation: landscape) and (max-height: 500px) and (max-width: 1200px)").matches) {
      this.style.removeProperty("--sf-panel-max");
      return;
    }
    if (this.dataset.openSelector && this.classList.contains('sf-mobile-active')) {
      const toolbar = this.root.querySelector<HTMLElement>('.sf-wrap>form');
      if (toolbar) this.style.setProperty('--sf-search-bottom', `${Math.round(toolbar.getBoundingClientRect().bottom - 1)}px`);
    }
    const form = this.activeInput.closest("form");
    const anchorEl = form ?? this.activeInput;
    const rect = anchorEl.getBoundingClientRect();
    const viewport = window.visualViewport;
    const visibleBottom = (viewport?.offsetTop ?? 0) + (viewport?.height ?? window.innerHeight);
    const available = Math.round(Math.max(180, Math.min(620, visibleBottom - rect.bottom - 10)));
    this.style.setProperty("--sf-panel-max", `${available}px`);
  }

  private readonly syncVisualViewport = (): void => {
    const viewport = window.visualViewport;
    const top = viewport?.offsetTop ?? 0;
    const left = viewport?.offsetLeft ?? 0;
    const width = viewport?.width ?? window.innerWidth;
    const height = viewport?.height ?? window.innerHeight;
    this.style.setProperty("--sf-viewport-top", `${top}px`);
    this.style.setProperty("--sf-viewport-left", `${left}px`);
    this.style.setProperty("--sf-viewport-width", `${width}px`);
    this.style.setProperty("--sf-viewport-height", `${height}px`);
    // Keep the active panel and body scroll correct across rotation.
    const open = Boolean(this.root && !this.root.querySelector<HTMLElement>(".sf-output")?.hidden);
    const compact = window.matchMedia("(max-width: 620px), (orientation: landscape) and (max-height: 500px) and (max-width: 1200px)").matches;
    if (open && compact) {
      if (this.classList.contains('sf-desktop-active')) {
        const input = this.root.querySelector<HTMLInputElement>('input[type="search"]')!;
        if (this.desktopReturnInput) this.desktopReturnInput.value = input.value;
        this.activeInput = this.desktopReturnInput ?? input;
        this.classList.remove('sf-desktop-active');
        document.body.style.overflow = this.previousBodyOverflow ?? '';
        this.previousBodyOverflow = undefined;
      }
      this.classList.add('sf-mobile-active');
    } else if (open) {
      this.classList.remove('sf-mobile-active');
      if (this.dataset.desktopFullscreen === 'true') this.showOutput();
    }
    // Cat loc se lasa liber sub panou. Cand ecranul vizibil e stramt — tastatura
    // deschisa, bara de browser, telefon mic — bara de jos a site-ului e oricum
    // acoperita, iar rezerva de 76px manca exact spatiul de care aveau nevoie
    // produsele: pe un ecran de 771px cu tastatura deschisa ramaneau ~169px de panou,
    // adica un produs si jumatate.
    //
    // Doua conditii in SAU, fiindca „tastatura" se vede altfel pe fiecare platforma:
    // pe iOS si pe Android Chrome nou `innerHeight` NU scade, deci se prinde prin
    // diferenta; pe Android in modul vechi `innerHeight` scade si el, iar diferenta
    // devine zero — acolo prinde pragul absolut. Spatiul putin e spatiu putin,
    // indiferent cine l-a mancat.
    const cramped = height < 430 || height < window.innerHeight - 120;
    this.classList.toggle('sf-cramped', compact && cramped);
    this.style.setProperty("--sf-panel-gap", cramped ? "14px" : "76px");
    // Tastatura schimba viewport-ul vizual: panoul trebuie recalculat imediat.
    this.updatePanelBounds();
    if (this.activeInput && !this.root.querySelector<HTMLElement>(".sf-output")?.hidden) {
      this.anchorTo(this.activeInput);
    }
  };

  connectedCallback(): void {
    // MerchantPro moves existing body children when it builds its mobile drawer.
    if (this.root) return;
    this.config = {
      storeKey: this.dataset.storeKey ?? "",
      apiUrl: (this.dataset.apiUrl ?? "https://smartyfind.com/api/v1").replace(/\/$/, ""),
      locale: this.dataset.locale ?? document.documentElement.lang ?? "ro",
      maxResults: Math.min(Math.max(Number(this.dataset.maxResults ?? 10), 1), 20),
      showPrices: this.dataset.showPrices !== "false",
      hijackSelector: this.dataset.hijackSelector ?? "",
      openSelector: this.dataset.openSelector,
      cartUrl: this.dataset.cartUrl || "/cos/",
      analytics: new URL(location.href).searchParams.has("sf_verify") ? "off" : analyticsMode(this.dataset.analytics)
    };
    this.analytics = new SearchAnalytics(this.config.analytics);
    // Dacă vizitatorul pleacă imediat după ce a tastat, căutarea nu se pierde.
    window.addEventListener("pagehide", () => this.analytics?.flush());
    // Viewport-ul vizual se citeste de la inceput: pe telefon se micsoreaza cand
    // apare tastatura, iar panoul trebuie sa stie cat loc are cu adevarat.
    this.syncVisualViewport();
    if (!this.config.storeKey) return;
    // Modulele se cer o dată, în paralel cu randarea. Până vin, comportamentul
    // e cel complet (fail-open), ca vizitatorul să nu aștepte un flag.
    void this.loadCartRecoveryOffer();
    this.sessionId = this.getSessionId();
    // Pagina de multumire poate aduce comanda ca atribut pe tag (vezi mu-pluginul
    // magazinului): scriptul inline al site-ului ar rula inainte sa existe elementul.
    this.sendStorePurchase();
    this.sendStoreCheckout();
    // Ce produs a deschis vizitatorul: din asta se afla ce sa ii recomandam.
    this.reportProductView();
    if(this.config.analytics!=='off') this.disposeProductEngagement=mountProductEngagement(metadata=>{
      this.discoverCache.clear(); void this.track('PRODUCT_IMPRESSION',{metadata});
    });
    const copy = copyFor(this.config.locale);
    this.root = this.attachShadow({ mode: "open" });

    let pointerStartX = 0;
    let pointerStartY = 0;
    let pointerStartTime = 0;
    let pointerStartAnchor: HTMLAnchorElement | null = null;

    this.root.addEventListener(
      "pointerdown",
      (event: Event) => {
        const pe = event as PointerEvent;
        pointerStartX = pe.clientX;
        pointerStartY = pe.clientY;
        pointerStartTime = Date.now();
        pointerStartAnchor = (event.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      },
      true
    );

    const handleProductNavigation = (event: Event) => {
      if (this.navigating) return;
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      // Cart recovery owns its click tracking; retain native keyboard/modifier navigation.
      if (anchor.classList.contains("sf-cart-action") || anchor.classList.contains('sf-outfit-card') || anchor.classList.contains('sf-outfit-credit')) return;
      const href = anchor.href;
      if (!href || href.endsWith("#") || href.endsWith("/#")) return;

      if (event.type === "pointerup" || event.type === "touchend") {
        // Focusing the store input opens the overlay beneath the pointer. Its
        // release may land on a newly revealed product, without a press on it.
        if (!pointerStartTime || pointerStartAnchor !== anchor) return;
        // Degetul care a derulat lista nu trebuie să deschidă produsul.
        if (this.suppressClickUntil > Date.now()) return;
        if (pointerStartTime > 0) {
          const pe = event as PointerEvent;
          // Pe `touchend` coordonatele sunt în `changedTouches`, nu pe eveniment:
          // fără asta distanța ieșea mereu 0 și orice derulare deschidea produsul.
          const touch = (event as TouchEvent).changedTouches?.[0];
          const clientX = typeof pe.clientX === "number" ? pe.clientX : touch ? touch.clientX : pointerStartX;
          const clientY = typeof pe.clientY === "number" ? pe.clientY : touch ? touch.clientY : pointerStartY;
          const dist = Math.hypot(clientX - pointerStartX, clientY - pointerStartY);
          if (dist > 12) return;
        }
      }

      event.preventDefault();
      event.stopPropagation();
      this.navigating = true;

      if (anchor.dataset.sfProductId) {
        if (anchor.dataset.sfQueryId) {
          this.rememberAttribution(anchor.dataset.sfQueryId, anchor.dataset.sfProductId, {
            productName: anchor.dataset.sfProductName,
            productUrl: href,
            imageUrl: anchor.dataset.sfImageUrl,
            assistantConversationId: anchor.dataset.sfAssistantSessionId
          });
          void this.track("PRODUCT_CLICK", {
            queryId: anchor.dataset.sfQueryId,
            productId: anchor.dataset.sfProductId,
            productName: anchor.dataset.sfProductName,
            position: Number(anchor.dataset.sfPosition || 1),
            metadata: anchor.dataset.sfAssistantSessionId ? {surface:'shopping-assistant',assistantConversationId:anchor.dataset.sfAssistantSessionId} : undefined
          });
        } else {
          this.rememberAttribution(undefined, anchor.dataset.sfProductId, {
            productName: anchor.dataset.sfProductName,
            productUrl: href,
            imageUrl: anchor.dataset.sfImageUrl,
            assistantConversationId: anchor.dataset.sfAssistantSessionId
          });
          void this.track("PRODUCT_CLICK", {
            productId: anchor.dataset.sfProductId,
            productName: anchor.dataset.sfProductName,
            position: Number(anchor.dataset.sfPosition || 1),
            metadata: anchor.dataset.sfAssistantSessionId ? {surface:"shopping-assistant",assistantConversationId:anchor.dataset.sfAssistantSessionId} : { surface: "discover", intent: anchor.dataset.sfIntent || this.discoverIntent }
          });
        }
      }

      window.location.assign(href);
    };

    this.root.addEventListener("pointerup", handleProductNavigation, true);
    this.root.addEventListener("touchend", handleProductNavigation, true);
    this.root.addEventListener("click", handleProductNavigation, true);
    this.root.innerHTML = `
      <style>
        ${STYLES}
      </style>
      <div class="sf-wrap">
        <form role="search">
          <span class="sf-brand" role="img" aria-label="SmartyFind" title="Căutare SmartyFind">
            <span class="sf-brand-mark" aria-hidden="true"><img src="https://smartyfind.com/branding/smartyfind-symbol-v1.webp" alt="" width="25" height="25" decoding="async" /></span>
            
          </span>
          <input type="search" autocomplete="off" aria-label="${copy.placeholder}" placeholder="${copy.placeholder}">
          <button type="button" class="sf-visual" aria-label="${copy.visualAction}" title="${copy.visualAction} · ${copy.visualPaste}" hidden>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 3.5 7.6 5.6H5A2.5 2.5 0 0 0 2.5 8.1v9.4A2.5 2.5 0 0 0 5 20h14a2.5 2.5 0 0 0 2.5-2.5V8.1A2.5 2.5 0 0 0 19 5.6h-2.6L15 3.5H9Zm3 5.4a4.6 4.6 0 1 1 0 9.2 4.6 4.6 0 0 1 0-9.2Z"/></svg>
          </button>
          <input type="file" class="sf-visual-file" accept="image/*" hidden>
          <button type="button" class="sf-close" aria-label="${copy.close}">×</button>
          <button type="submit">${copy.action}</button>
        </form>
      </div>
      <div class="sf-backdrop" aria-hidden="true"></div>
      <aside class="sf-cart-recovery" role="dialog" aria-live="polite" aria-label="${copy.cartRecoveryTitle ?? "Ai produse în coș"}" hidden>
        <button type="button" class="sf-cart-dismiss" aria-label="${copy.cartRecoveryClose ?? copy.close}">×</button>
        <div class="sf-cart-media" hidden><img alt=""></div>
        <div class="sf-cart-copy">
          <strong class="sf-cart-title">${copy.cartRecoveryTitle ?? "Ai produse în coș"}</strong>
          <span class="sf-cart-message">${copy.cartRecoveryText ?? "Continuă comanda de unde ai rămas."}</span>
          <small hidden></small>
          <div class="sf-cart-pricing" hidden><s></s><b></b><span class="sf-cart-discount"></span></div>
          <div class="sf-cart-coupon" hidden><b></b><span></span></div>
        </div>
        <a class="sf-cart-action" href="${this.config.cartUrl}">${copy.cartRecoveryAction ?? "Vezi coșul"}</a>
      </aside>
      <div class="sf-output" hidden>
        <div class="sf-grab" aria-hidden="true"></div>
        <div class="sf-suggest" hidden>
          <h3 class="sf-col-title">${copy.suggestionsTitle}</h3>
          <ul class="sf-suggest-list"></ul>
        </div>
        <div class="sf-browse-layout">
          <aside class="sf-browse-side" hidden>
          <div class="sf-discover-side">
            <h3 class="sf-col-title sf-recent-title" hidden>${copy.recentTitle ?? "Căutările tale"}</h3>
            <ul class="sf-popular sf-recent" hidden></ul>
            <h3 class="sf-col-title">${copy.popularTitle}</h3>
            <ul class="sf-popular sf-popular-main"></ul>
            <div class="sf-side-foot">${copy.poweredBy}</div>
          </div>
            <section class="sf-editorial-reco" aria-label="${this.config.locale==='ro'?'Îți recomandăm':'Recommended for you'}" hidden></section>
          </aside>
          <section class="sf-browse-main">
        <div class="sf-chipbox sf-refine"></div>
        <div class="sf-discover" hidden>
          <div class="sf-discover-main">
            <p class="sf-visual-empty" hidden></p>
            <h3 class="sf-reco-title">${copy.recoTitle}</h3>
            <div class="sf-reco-grid"></div>
          </div>
        </div>
        <div class="sf-summary">
          <span class="sf-ai-pill"><span class="sf-ai-dot"></span>${copy.aiLabel}</span>
          <div class="sf-search-status" role="status" aria-live="polite"></div>
          <button type="button" class="sf-visual-entry" aria-label="${copy.visualAction}" title="${copy.visualAction} · ${copy.visualPaste}" hidden>
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M9 3.5 7.6 5.6H5A2.5 2.5 0 0 0 2.5 8.1v9.4A2.5 2.5 0 0 0 5 20h14a2.5 2.5 0 0 0 2.5-2.5V8.1A2.5 2.5 0 0 0 19 5.6h-2.6L15 3.5H9Zm3 5.4a4.6 4.6 0 1 1 0 9.2 4.6 4.6 0 0 1 0-9.2Z"/></svg>
            <span>${copy.visualAction}</span>
          </button>
          <button type="button" class="sf-panel-close" aria-label="${copy.close}">×</button>
        </div>
        <p class="sf-visual-advice" hidden style="padding:12px;line-height:1.5;white-space:normal"></p>
        <div class="sf-understood"></div>
        <div class="sf-results"></div>
        <div class="sf-foot" hidden>
          <button type="button" class="sf-more" hidden></button>
          <a class="sf-see-all" hidden></a>
        </div>
          </section>
        </div>
      </div>
    `;
    this.assistant = mountShoppingAssistant(this.root, this.config, () => this.flags.enabled('shopping_assistant'), {visitorId:this.sessionId, track:(type,detail)=>{void this.track(type,detail);}, analytics:(action,data)=>this.config.analytics==='off'?false:emitAssistantAnalytics(action,data)});
    // Build optional controls before the first interaction, not after network responses.
    void mountQuiz(this.root,this.config,this.sessionId,()=>this.flags.enabled('discovery.quiz'));
    void this.loadModules();
    const form = this.root.querySelector("form")!;
    const input = this.root.querySelector("input")!;
    const close = this.root.querySelector<HTMLButtonElement>(".sf-close")!;
    const backdrop = this.root.querySelector<HTMLElement>(".sf-backdrop")!;
    const cartRecovery = this.root.querySelector<HTMLElement>(".sf-cart-recovery")!;
    this.root.querySelector<HTMLButtonElement>(".sf-cart-dismiss")!.addEventListener("click", () => this.dismissCartRecovery());
    this.root.querySelector<HTMLAnchorElement>(".sf-cart-action")!.addEventListener("click", async (event) => {
      const state = this.readCartRecovery();
      const action = event.currentTarget as HTMLAnchorElement;
      const guarded = !!this.dataset.cartStatusUrl && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && event.button === 0;
      if (guarded) {
        event.preventDefault();
        if (!state || !await this.verifyCartRecovery(state)) { cartRecovery.hidden = true; return; }
      }
      cartRecovery.hidden = true;
      void this.track("SUGGESTION_CLICK", { metadata: {
        surface: "cart-recovery",
        productName: state?.productName,
        productUrl: state?.productUrl,
        quantity: state?.quantity,
        couponCode: this.cartRecoveryOffer.couponEnabled ? this.cartRecoveryOffer.couponCode : undefined,
        couponPercent: this.cartRecoveryOffer.couponEnabled ? this.cartRecoveryOffer.couponPercent : undefined
      } });
      if (guarded) window.location.assign(action.href);
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      if (this.debounceTimer) window.clearTimeout(this.debounceTimer);
      void this.search(input.value);
    });
    // „Caută după poză" (modulul visual_search): poza se pregătește în browser și
    // pleacă spre API ca data URL, fără să urce fișierul pe un server intermediar.
    const visualButton = this.root.querySelector<HTMLButtonElement>(".sf-visual")!;
    const visualEntry = this.root.querySelector<HTMLButtonElement>(".sf-visual-entry")!;
    const visualFile = this.root.querySelector<HTMLInputElement>(".sf-visual-file")!;
    const visualEnabled = this.flags.enabled("visual_search");
    visualButton.hidden = !visualEnabled;
    visualEntry.hidden = !visualEnabled;
    visualButton.addEventListener("click", () => visualFile.click());
    // În modul hijack bara widgetului e ascunsă, deci intrarea în căutarea după poză
    // trebuie să existe și în panou (lângă chip-uri), nu doar în bara proprie.
    visualEntry.addEventListener("click", () => visualFile.click());
    visualFile.addEventListener("change", () => void this.runVisualSearch(visualFile));
    input.addEventListener("input", () => {
      this.activeInput = input;
      this.visualSearchActive = false;
      this.activateMobileSearch();
      if (this.debounceTimer) window.clearTimeout(this.debounceTimer);
      const query = input.value.trim();
      if (query.length < 2) {
        // Panoul de descoperire are propriul continut. Un status ramas de la cautarea dinainte
        // ar spune „nu am gasit produse potrivite" chiar deasupra recomandarilor — vazut pe
        // site: dupa o cautare fara rezultate, golirea campului lasa mesajul pe loc.
        this.root.querySelector<HTMLElement>(".sf-search-status")!.textContent = "";
        this.showDiscover();
        return;
      }
      this.scheduleSuggestions(input, query);
      this.debounceTimer = window.setTimeout(() => void this.search(query, true), LIVE_SEARCH_DELAY_MS);
    });
    input.addEventListener("focus", () => {
      if (this.restoringSearchFocus) return;
      this.activeInput = input;
      this.activateMobileSearch();
      if (input.value.trim().length < 2) this.showDiscover();
    });
    input.addEventListener("click", () => {
      this.activateMobileSearch();
      if (input.value.trim().length < 2) this.showDiscover();
    });
    form.addEventListener("pointerdown", () => {
      this.activateMobileSearch();
      if (input.value.trim().length < 2) this.showDiscover();
    });
    this.root.addEventListener("focusin", () => this.activateMobileSearch());
    this.root.addEventListener("keydown", (event) => {
      if (!(event instanceof KeyboardEvent)) return;
      if (event.key === "ArrowDown" && this.focusPanelItem(1, event.target as HTMLElement)) event.preventDefault();
      if (event.key === "ArrowUp" && this.focusPanelItem(-1, event.target as HTMLElement)) event.preventDefault();
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.closeSearch(true);
      }
      if (event.key === "Tab" && this.classList.contains("sf-desktop-active")) {
        const items = Array.from(this.root.querySelectorAll<HTMLElement>('input,button,a[href]'))
          .filter((item) => item.getClientRects().length > 0 && !item.hasAttribute('disabled'));
        const current = this.root.activeElement;
        if (event.shiftKey && current === items[0]) {
          event.preventDefault(); items.at(-1)?.focus();
        } else if (!event.shiftKey && current === items.at(-1)) {
          event.preventDefault(); items[0]?.focus();
        }
      }
    });
    document.addEventListener("pointerdown", this.closeOnOutsidePointer, true);
    document.addEventListener("paste", this.onPaste);
    input.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.closeSearch(true);
      }
    });
    close.addEventListener("click", () => {
      input.blur();
      this.closeSearch(true);
    });
    this.root.querySelector<HTMLButtonElement>('.sf-panel-close')!.addEventListener('click', () => {
      this.activeInput?.blur();
      this.closeSearch(false);
    });
    backdrop.addEventListener("click", () => {
      input.blur();
      this.hideOutput();
      this.deactivateMobileSearch();
    });
    window.visualViewport?.addEventListener("resize", this.syncVisualViewport);
    window.visualViewport?.addEventListener("scroll", this.syncVisualViewport);
    this.setupPanelGestures();
    if (this.config.hijackSelector) this.attachHijack();
    if (this.dataset.openSelector) document.addEventListener("click", this.onExternalSearchOpen, true);
    void checkInstallation(this,this.config,data=>{
      const verificationInput=this.root.querySelector<HTMLInputElement>('input[type="search"]');
      this.resultQuery=data.results[0]?.name??'';
      if(verificationInput){verificationInput.value=this.resultQuery;this.activeInput=verificationInput;}
      this.activateMobileSearch();
      this.showOutput();
      this.renderResults(data.results,data.queryId);
    });
    this.addEventListener("smartyfind:track", (event) => {
      if (!(event instanceof CustomEvent) || !event.detail || typeof event.detail !== "object") return;
      const detail = event.detail as Record<string, unknown>;
      const type = String(detail.type ?? "");
      if (!["ADD_TO_CART", "REMOVE_FROM_CART", "CHECKOUT_START", "PURCHASE", "PRODUCT_IMPRESSION"].includes(type)) return;
      if (type === "ADD_TO_CART") this.rememberCart(detail);
      if (type === "REMOVE_FROM_CART") this.forgetCart(detail);
      if (type === "PURCHASE") this.clearCartRecovery();
      if (type === "CHECKOUT_START") this.snoozeCartRecovery();
      void this.track(type, {
        eventId: typeof detail.confirmationId === "string" && /^[0-9a-f-]{36}$/i.test(detail.confirmationId) ? detail.confirmationId : undefined,
        queryId: typeof detail.queryId === "string" ? detail.queryId : this.activeQueryId,
        productId: typeof detail.productId === "string" ? detail.productId : undefined,
        variantId: typeof detail.variantId === "string" ? detail.variantId : undefined,
        orderId: typeof detail.orderId === "string" ? detail.orderId : undefined,
        quantity: typeof detail.quantity === "number" ? detail.quantity : undefined,
        value: typeof detail.value === "number" ? detail.value : undefined,
        currency: typeof detail.currency === "string" ? detail.currency : undefined,
        metadata: detail.metadata && typeof detail.metadata === "object" ? detail.metadata : undefined
      });
    });
    this.commerceDelivery ??= new EventDelivery(`${this.config.apiUrl}/events`, this.headers(), `smartyfind_events_${this.config.storeKey.slice(-16)}`);
    if (this.dataset.platform === "merchantpro") connectMerchantProCommerce(this);
    this.seedStoreCart();
    this.scheduleCartRecovery(6000);
    document.addEventListener("visibilitychange", this.onCartRecoveryReturn);
    window.addEventListener("pageshow", this.onCartRecoveryReturn);
  }

  disconnectedCallback(): void {
    this.outfitPanel?.clear();
    queueMicrotask(() => {
    // A synchronous DOM move is not destruction. Keep the mounted widget intact.
    if (this.isConnected) return;
    this.disposeVoice?.();
    this.disposeRecommendations?.();
    this.disposeProductEngagement?.();
    if (this.root) this.hideOutput();
    this.assistant?.destroy();
    window.visualViewport?.removeEventListener("resize", this.syncVisualViewport);
    window.visualViewport?.removeEventListener("scroll", this.syncVisualViewport);
    document.removeEventListener("pointerdown", this.closeOnOutsidePointer, true);
    document.removeEventListener("paste", this.onPaste);
    document.removeEventListener("visibilitychange", this.onCartRecoveryReturn);
    window.removeEventListener("pageshow", this.onCartRecoveryReturn);
    this.detachHijack();
    this.activeRequest?.abort();
    this.moreRequest?.abort();
    this.panelObserver?.disconnect();
    if (this.panelScrollFrame) window.cancelAnimationFrame(this.panelScrollFrame);
    window.removeEventListener("resize", this.updateScrollHint);
    this.discoverRequest?.abort();
    if (this.debounceTimer) window.clearTimeout(this.debounceTimer);
    if (this.cartRecoveryTimer) window.clearTimeout(this.cartRecoveryTimer);
    this.disconnectChipObservers();
    });
  }

  private async loadModules(): Promise<void> {
    const voiceSettings = fetch(`${this.config.apiUrl}/content-search/settings`,{headers:this.headers()})
      .then(async response=>response.ok&&(await response.json()).voiceEnabled===true).catch(()=>false);
    this.flags = await loadModuleFlags(this.config);
    if(this.flags.loaded && this.flags.enabled("recommendations")) this.disposeRecommendations=mountRecommendationFlows(this.config,this.sessionId,(type,data)=>{void this.track(type,data)});
    if (this.flags.loaded && this.flags.enabled("search.voice")) {
      void voiceSettings.then(enabled=>{if(enabled&&this.isConnected) this.disposeVoice=mountVoiceSearch(this.root,this.config.locale??"ro",query=>{void this.search(query,true)});});
    }
    this.assistant?.refresh();
    if (this.flags.loaded && this.flags.enabled("discovery.category-merchandising")) void mountCategoryMerchandising(this.config);
    if (!this.flags.enabled("discovery.quiz")) this.root.querySelector('.sf-quiz-launch')?.remove();
    if (!this.flags.enabled("search.core")) this.disable();
    else if (this.flags.enabled("discover.panel")) void this.loadDiscover("default", false, true);
  }

  private async loadCartRecoveryOffer(): Promise<void> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    try {
      const response = await fetch(`${this.config.apiUrl}/cart-recovery/public`, { headers: this.headers(), signal: controller.signal });
      if (!response.ok) return;
      const payload = await response.json() as Partial<CartRecoveryOffer>;
      this.cartRecoveryOffer = {
        enabled: payload.enabled !== false,
        delayMinutes: Number.isInteger(payload.delayMinutes) && Number(payload.delayMinutes) >= 1 && Number(payload.delayMinutes) <= 60 ? Number(payload.delayMinutes) : DEFAULT_CART_RECOVERY_DELAY_MINUTES,
        couponEnabled: payload.couponEnabled === true,
        couponCode: typeof payload.couponCode === "string" ? payload.couponCode : null,
        couponPercent: payload.couponPercent !== null && payload.couponPercent !== undefined && Number.isFinite(Number(payload.couponPercent)) ? Number(payload.couponPercent) : null,
        minimumSubtotal: payload.minimumSubtotal !== null && payload.minimumSubtotal !== undefined && Number.isFinite(Number(payload.minimumSubtotal)) ? Number(payload.minimumSubtotal) : null,
        headline: typeof payload.headline === "string" && payload.headline.trim() ? payload.headline.trim() : this.cartRecoveryOffer.headline,
        message: typeof payload.message === "string" && payload.message.trim() ? payload.message.trim() : this.cartRecoveryOffer.message,
      };
    } catch {
      // Oferta implicită rămâne activă dacă setările nu pot fi citite temporar.
    } finally {
      window.clearTimeout(timeout);
      this.cartRecoveryOfferLoaded = true;
    }
  }

  /**
   * Modulul de bază e oprit din admin: widgetul se retrage complet din
   * căutarea magazinului, fără să lase inputuri modificate în urmă.
   */
  private disable(): void {
    this.detachHijack();
    this.hideOutput();
    this.setAttribute("hidden", "");
  }

  /** Dropdown-ul de dinainte de tastare: căutări populare, chips-uri, recomandări. */
  private showDiscover(): void {
    if (this.visualSearchActive) return;
    this.outfitPanel?.clear();
    // Panoul poate fi oprit din admin; atunci căutarea pornește direct la
    // tastare, fără populare, chips-uri și recomandări.
    if (!this.flags.enabled("discover.panel")) return;
    this.sidebarResults = [];
    this.sidebarQueryId = "";
    this.root.querySelector<HTMLElement>(".sf-discover")!.hidden = false;
    // Bara de sus NU se ascunde în starea de descoperire: acolo stă butonul „Caută
    // după poză", iar el trebuie vizibil înainte de orice tastare. Înainte linia de
    // aici îl scotea din vedere (și oricum nu avea efect, fiindcă `.sf-summary` are
    // `display:flex` și nu exista regula `[hidden]`).
    // Intrarea normală în panou (focus pe căutare, tastare) șterge mesajul lăsat
    // de o poză care nu a găsit nimic: textul acela e doar despre căutarea foto.
    this.root.querySelector<HTMLElement>(".sf-visual-empty")?.setAttribute("hidden", "");
    const understood = this.root.querySelector<HTMLElement>(".sf-understood")!;
    understood.textContent = "";
    understood.style.display = "none";
    this.root.querySelector<HTMLElement>(".sf-results")!.replaceChildren();
    this.root.querySelector<HTMLAnchorElement>(".sf-see-all")!.hidden = true;
    this.hideSuggestions();
    this.resultQuery = "";
    this.resultLimit = 0;
    this.resultCount = 0;
    this.updateResultsFooter();
    this.showOutput();
    void this.loadDiscover();
  }

  private hideDiscover(): void {
    this.discoveryViewTracked = false;
    this.root.querySelector<HTMLElement>(".sf-discover")!.hidden = true;
    this.root.querySelector<HTMLElement>(".sf-summary")!.hidden = false;
  }

  private trackDiscoverView(intent: DiscoverIntent): void {
    if (this.discoveryViewTracked) return;
    this.discoveryViewTracked = true;
    void this.track("SUGGESTION_VIEW", { metadata: { surface: "discover", intent } });
  }

  private async loadDiscover(intent: DiscoverIntent = this.discoverIntent, force = false, prefetch = false): Promise<void> {
    const cached = this.discoverCache.get(intent);
    if (!force && cached && Date.now() - cached.at < DISCOVER_TTL_MS) {
      this.discoverIntent = intent;
      if (!prefetch) {
        this.renderDiscover(cached.data);
        this.trackDiscoverView(intent);
      }
      return;
    }
    if (this.discoverLoading) {
      if (!prefetch) this.pendingDiscoverIntent = intent;
      return;
    }
    this.discoverLoading = true;
    this.discoverRequest?.abort();
    this.discoverRequest = new AbortController();
    try {
      const response = await fetch(`${this.config.apiUrl}/discover`, {
        method: "POST",
        headers: this.headers(),
        signal: this.discoverRequest.signal,
        // API-ul intoarce modele distincte, nu cate un card pentru fiecare marime.
        body: JSON.stringify({ language: this.config.locale, limit: 20, intent, sessionId: this.sessionId })
      });
      if (!response.ok) throw new Error(`DISCOVER_${response.status}`);
      const data = await response.json() as DiscoverResponse;
      this.discoverIntent = intent;
      this.discoverCache.set(intent, { at: Date.now(), data });
      // Prepare photos before focus, without opening the panel or recording a view.
      this.discoverImages = data.recommendations.slice(0, 8).flatMap(product => {
        if (!product.imageUrl) return [];
        const image = new Image();
        image.decoding = "async";
        image.fetchPriority = "low";
        image.src = product.imageUrl;
        return [image];
      });
      const visible = !this.root.querySelector<HTMLElement>(".sf-output")!.hidden &&
        !this.root.querySelector<HTMLElement>(".sf-discover")!.hidden && !this.visualSearchActive;
      if (visible && (!this.pendingDiscoverIntent || this.pendingDiscoverIntent === intent)) {
        this.renderDiscover(data);
        this.trackDiscoverView(intent);
      }
    } catch (error) {
      // Dacă dropdown-ul nu poate fi încărcat, căutarea rămâne funcțională.
      if (error instanceof DOMException && error.name === "AbortError") return;
    } finally {
      this.discoverLoading = false;
      const pending = this.pendingDiscoverIntent;
      this.pendingDiscoverIntent = undefined;
      if (pending && pending !== intent) void this.loadDiscover(pending);
    }
  }

  private renderDiscover(data: DiscoverResponse): void {
    const copy = copyFor(this.config.locale);
    void this.renderSearchBanner(this.resultQuery);
    this.renderSideRecommendations(data);
    const recent = this.root.querySelector<HTMLElement>(".sf-recent")!;
    const recentTitle = this.root.querySelector<HTMLElement>(".sf-recent-title")!;
    recent.replaceChildren();
    for (const item of (data.recentSearches ?? []).slice(0, 3)) {
      const entry = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = this.decodeEntities(item.term);
      button.addEventListener("click", () => this.pickQuery(item.term, "history"));
      entry.append(button);
      recent.append(entry);
    }
    recent.hidden = recent.childElementCount === 0;
    recentTitle.hidden = recent.hidden;

    const popular = this.root.querySelector<HTMLElement>(".sf-popular-main")!;
    popular.replaceChildren();
    for (const item of data.popularSearches.slice(0, 6)) {
      const entry = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = this.decodeEntities(item.term);
      button.addEventListener("click", () => this.pickQuery(item.term, `popular:${item.source}`));
      entry.append(button);
      popular.append(entry);
    }
    this.root.querySelector<HTMLElement>(".sf-discover-side")!.hidden = data.popularSearches.length === 0 && recent.hidden;
    this.root.querySelector<HTMLElement>('.sf-browse-side')!.hidden = !this.flags.enabled('discover.panel') || this.root.querySelector<HTMLElement>('.sf-discover-side')!.hidden && this.root.querySelector<HTMLElement>('.sf-editorial-reco')!.hidden;

    const types = discoveryTypeChips(data.groups, copy, 64);
    if (types?.length) {
      this.refinementSync = mountCompactRefinement(this.root.querySelector<HTMLElement>(".sf-chipbox")!, types,
        () => this.activeInput?.value ?? this.resultQuery,
        query => this.pickQuery(query, "refinement"));
    }

    const recommendationTitle = this.root.querySelector<HTMLElement>(".sf-reco-title")!;
    recommendationTitle.textContent = data.personalized ? copy.recoTitle : ({ro:'De sezon și populare',en:'Seasonal and popular',it:'Di stagione e popolari',de:'Saisonal und beliebt',fr:'De saison et populaires'}[this.config.locale]??'Discover products');

    const grid = this.root.querySelector<HTMLElement>(".sf-reco-grid")!;
    grid.replaceChildren();
    // Opt produse, pe două rânduri: serverul trimite deja opt, iar cardul are acum cinci
    // rânduri în loc de opt, deci al doilea rând încape. Cu patru, panoul rămânea pe
    // jumătate gol și vizitatorul nu vedea ce are magazinul.
    // Acelasi produs nu are ce căuta de două ori în grilă, chiar dacă serverul ar
    // trimite două variante de mărime.
    const gridProducts = this.uniqueByProduct(data.recommendations).slice(0, 16);
    grid.classList.toggle('sf-few-recommendations',gridProducts.length>0&&gridProducts.length<=2);
    gridProducts.forEach((product, index) => {
      grid.append(this.recommendationCard(product, index));
      void this.track("PRODUCT_IMPRESSION", {
        productId: product.productId,
        position: index + 1,
        metadata: { surface: "discover", intent: this.discoverIntent }
      });
    });
  }

  /** Port of FVE Smart's editorial sidebar: 64px image, compact price card, separate from results. */
  private renderSideRecommendations(data: DiscoverResponse): void {
    const box=this.root.querySelector<HTMLElement>('.sf-editorial-reco')!;
    box.replaceChildren();
    if(this.resultQuery||this.visualSearchActive){box.hidden=true;return;}
    const excluded=this.uniqueByProduct(data.recommendations).slice(0,8);
    const items=sidebarRecommendationItems(data.recommendations,excluded);
    box.hidden=!items.length;
    if(!items.length) return;
    const heading=document.createElement('h3');heading.className='sf-editorial-head';
    heading.textContent=this.config.locale==='ro'?'Îți recomandăm…':'Recommended for you';
    const track=document.createElement('div');track.className='sf-editorial-track';
    track.tabIndex=0;track.setAttribute('aria-label',heading.textContent);
    const controls=document.createElement('div');controls.className='sf-editorial-controls';
    for(const [label,direction] of [[this.config.locale==='ro'?'Recomandările anterioare':'Previous recommendations',-1],[this.config.locale==='ro'?'Recomandările următoare':'Next recommendations',1]] as const){
      const button=document.createElement('button');button.type='button';button.textContent=direction<0?'‹':'›';button.setAttribute('aria-label',label);
      button.onclick=()=>track.scrollBy({left:direction*220,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth'});controls.append(button);
    }
    const header=document.createElement('div');header.className='sf-editorial-header';header.append(heading,controls);
    items.forEach((product,index)=>{
      const card=document.createElement('a');card.className='sf-editorial-card';card.href=this.decodeEntities(product.productUrl ?? '#');
      card.dataset.sfProductId=product.productId;card.dataset.sfPosition=String(index+1);card.dataset.sfProductName=this.decodeEntities(product.name);card.dataset.sfImageUrl=product.imageUrl ?? '';
      if(this.sidebarQueryId) card.dataset.sfQueryId=this.sidebarQueryId;
      const queryId=this.sidebarQueryId;
      card.addEventListener('click',()=>{void this.track('PRODUCT_CLICK',{...(queryId?{queryId}:{}),productId:product.productId,position:index+1,metadata:{surface:'sidebar-recommendations',intent:this.discoverIntent}});});
      const image=document.createElement('img');image.alt=this.decodeEntities(product.name);image.loading='lazy';if(product.imageUrl) image.src=product.imageUrl;
      const info=document.createElement('span');info.className='sf-editorial-info';
      const name=document.createElement('strong');name.textContent=this.decodeEntities(product.name);info.append(name);
      if(this.config.showPrices && (product.salePrice!=null || product.price!=null)) {
        const price=document.createElement('span');price.textContent=this.money(product.salePrice ?? product.price,product.currency);info.append(price);
        if(this.isOnSale(product.price,product.salePrice)) {
          const discount=document.createElement('em');discount.textContent=`-${Math.round((1-product.salePrice!/product.price!)*100)}%`;info.append(discount);
        }
      }
      card.append(image,info);track.append(card);
    });
    const hint=document.createElement('small');hint.className='sf-editorial-hint';hint.textContent=this.config.locale==='ro'?'Glisează pentru mai multe produse':'Swipe for more products';
    box.append(header,track,hint);
  }

  /**
   * Feed-urile listează fiecare mărime ca produs separat. Serverul grupează deja pe
   * familia de produs, dar widgetul nu afișează niciodată același produs de două ori.
   */
  private uniqueByProduct<T extends { productId: string; productUrl?: string }>(items: T[]): T[] {
    const seen = new Set<string>();
    const result: T[] = [];
    for (const item of items) {
      if (!item?.productId) continue;
      const key = productFamilyKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(item);
    }
    return result;
  }

  private pickQuery(term: string, source: string): void {
    const input = this.activeInput ?? this.root.querySelector<HTMLInputElement>("input");
    if (input) input.value = term;
    this.hideDiscover();
    void this.track("SUGGESTION_CLICK", { metadata: { source, query: term } });
    void this.search(term);
  }

  private mediaBox(product: DiscoverRecommendation, className: string, withBadge: boolean, index = 0): HTMLElement {
    const media = document.createElement("div");
    media.className = className;
    if (product.imageUrl) {
      const image = document.createElement("img");
      image.alt = this.decodeEntities(product.name);
      image.decoding = "async";
      // Primele patru se vad imediat ce se deschide panoul; lazy le lasa sa apara
      // cu intarziere, adica exact patratelele gri pe care le vede utilizatorul.
      image.loading = index < 4 ? "eager" : "lazy";
      image.src = product.imageUrl;
      media.append(image);
    }
    if (withBadge && product.discountPercent) {
      const badge = document.createElement("span");
      badge.className = "sf-reco-badge";
      badge.textContent = `-${product.discountPercent}%`;
      media.append(badge);
    }
    markCampaignOffer(media,this.config.showPrices&&!!product.discountPercent,this.campaignBadgeLabel);
    return media;
  }

  private productName(product: DiscoverRecommendation, className: string): HTMLElement {
    const name = document.createElement("div");
    name.className = className;
    name.textContent = this.decodeEntities(product.name);
    return name;
  }

  private priceLine(product: DiscoverRecommendation, className: string): HTMLElement | null {
    if (!this.config.showPrices || (product.salePrice === null && product.price === null)) return null;
    const price = document.createElement("div");
    price.className = className;
    if (this.isOnSale(product.price, product.salePrice)) {
      const old = document.createElement("s");
      old.textContent = this.money(product.price, product.currency);
      price.append(old);
    }
    price.append(document.createTextNode(this.money(product.salePrice ?? product.price, product.currency)));
    if (product.discountPercent && this.isOnSale(product.price, product.salePrice)) {
      const discount = document.createElement("span");
      discount.className = "sf-price-discount";
      discount.textContent = `-${product.discountPercent}%`;
      price.append(discount);
    }
    return price;
  }

  private recommendationCard(product: DiscoverRecommendation, index: number, showBadges = true): HTMLElement {
    const copy = copyFor(this.config.locale);
    const card = document.createElement("a");
    card.className = "sf-reco-card";
    card.href = this.decodeEntities(product.productUrl ?? "#");
    card.dataset.sfProductId = product.productId;
    card.dataset.sfPosition = String(index + 1);
    card.dataset.sfIntent = this.discoverIntent;
    card.dataset.sfProductName = this.decodeEntities(product.name);
    card.dataset.sfImageUrl = product.imageUrl ?? "";
    card.addEventListener("click", () => {
      void this.track("PRODUCT_CLICK", {
        productId: product.productId,
        position: index + 1,
        metadata: { surface: "discover", intent: this.discoverIntent }
      });
    });
    card.append(this.mediaBox(product, "sf-reco-media", showBadges, index));
    card.append(this.productName(product, "sf-reco-name"));
    const brandLabel = this.usefulLabel(product.brand) ?? this.usefulLabel(product.category);
    if (brandLabel || product.color) {
      const brand = document.createElement("div");
      brand.className = "sf-reco-brand";
      brand.textContent = [brandLabel?this.decodeEntities(brandLabel):'',product.color?`${this.config.locale==='ro'?'Culoare':'Colour'}: ${this.decodeEntities(product.color)}`:''].filter(Boolean).join(' · ');
      card.append(brand);
    }
    const price = this.priceLine(product, "sf-reco-price");
    if (price) card.append(price);
    if (product.availableSizes?.length) {
      const sizes = document.createElement("div");
      sizes.className = "sf-sizes";
      sizes.setAttribute("aria-label", copy.availableSizes);
      const label = document.createElement("span");
      label.className = "sf-sizes-label";
      label.textContent = `${copy.availableSizes}:`;
      sizes.append(label);
      for (const sizeValue of product.availableSizes.slice(0, 5)) {
        const size = document.createElement("span");
        size.className = "sf-size";
        size.textContent = sizeValue;
        sizes.append(size);
      }
      if (product.availableSizes.length > 5) {
        const more = document.createElement("span");
        more.className = "sf-size-more";
        more.textContent = `+${product.availableSizes.length - 5}`;
        sizes.append(more);
      }
      card.append(sizes);
    }
    const badge = productBadge(product, this.config.locale, { showPrices: this.config.showPrices, recommended: product.personalized === true });
    if (showBadges && badge) {
      const reason = document.createElement("span");
      reason.className = `sf-reco-reason is-${badge.kind}`;
      reason.textContent = badge.text;
      if(badge.title)reason.title=badge.title;
      card.append(reason);
    }
    const action = document.createElement("span");
    action.className = "sf-card-action";
    action.textContent = product.availableSizes?.length ? copy.chooseSize : copy.viewProduct;
    card.append(action);
    return card;
  }

  private activateMobileSearch(): void {
    if (!window.matchMedia("(max-width: 620px), (orientation: landscape) and (max-height: 500px) and (max-width: 1200px)").matches) return;
    this.syncVisualViewport();
    this.classList.add("sf-mobile-active");
  }

  private deactivateMobileSearch(): void {
    this.classList.remove("sf-mobile-active");
  }

  /**
   * Modul hijack: widgetul nu afișează propria bară, ci se atașează de inputurile
   * de căutare ale site-ului și deschide panoul sub inputul folosit.
   */
  private attachHijack(): void {
    let inputs: HTMLInputElement[] = [];
    try {
      inputs = Array.from(document.querySelectorAll<HTMLInputElement>(this.config.hijackSelector));
    } catch {
      return;
    }
    if (!this.hijackObserver) {
      this.hijackObserver = new MutationObserver((records) => {
        if (records.some(record => record.addedNodes.length > 0)) this.attachHijack();
      });
      this.hijackObserver.observe(document.body, { childList: true, subtree: true });
    }
    if (!inputs.length) return;
    this.classList.add("sf-hijack");
    document.addEventListener("click",this.onFooterSearchOpen,true);
    for (const input of inputs) {
      if (this.hijackInputs.includes(input)) continue;
      // Fără asta, browserul își afișează propriul dropdown de istoric peste panou.
      input.setAttribute("autocomplete", "off");
      input.setAttribute("spellcheck", "false");
      input.addEventListener("focus", this.onHijackFocus);
      input.addEventListener("click", this.onHijackFocus);
      input.addEventListener("input", this.onHijackInput);
      input.addEventListener("keydown", this.onHijackKeydown);
      this.hijackInputs.push(input);
      input.form?.addEventListener("submit", this.onHijackSubmit);
      if (document.activeElement === input) this.onHijackFocus({ target: input } as unknown as Event);
    }
    if (inputs.some(input => !input.dataset.sfSearchRestored) && new URLSearchParams(window.location.search).get("sf_search") === "1") {
      for (const input of inputs) input.dataset.sfSearchRestored = "true";
      const input = this.hijackInputs.find((item) => item.value.trim().length >= 2);
      if (input) {
        this.activeInput = input;
        this.activateMobileSearch();
        this.anchorTo(input);
        void this.search(input.value.trim(), true);
      }
    }
    window.addEventListener("scroll", this.reposition, { passive: true });
    window.addEventListener("resize", this.reposition);
  }

  private detachHijack(): void {
    document.removeEventListener("click",this.onFooterSearchOpen,true);
    document.removeEventListener("click", this.onExternalSearchOpen, true);
    this.hijackObserver?.disconnect();
    this.hijackObserver = undefined;
    for (const input of this.hijackInputs) {
      input.removeEventListener("focus", this.onHijackFocus);
      input.removeEventListener("click", this.onHijackFocus);
      input.removeEventListener("input", this.onHijackInput);
      input.removeEventListener("keydown", this.onHijackKeydown);
      input.form?.removeEventListener("submit", this.onHijackSubmit);
    }
    this.hijackInputs = [];
    window.removeEventListener("scroll", this.reposition);
    window.removeEventListener("resize", this.reposition);
    if (this.repositionFrame) {
      window.cancelAnimationFrame(this.repositionFrame);
      this.repositionFrame = 0;
    }
  }

  /** Panoul se așază sub inputul de căutare folosit, fără să iasă din ecran. */
  private anchorTo(input: HTMLInputElement): void {
    const form = input.closest("form");
    const anchorEl = form ?? input;
    const rect = anchorEl.getBoundingClientRect();
    const isMobile = window.matchMedia("(max-width: 620px), (orientation: landscape) and (max-height: 500px) and (max-width: 1200px)").matches;
    const margin = isMobile ? 8 : 12;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    if (rect.bottom < 0 || rect.top > viewportHeight) return;

    this.updatePanelBounds();

    const available = Math.max(280, window.innerWidth - margin * 2);
    const width = isMobile ? available : Math.min(1120, Math.max(rect.width, 1120), available);
    const left = isMobile ? margin : Math.min(Math.max(rect.left, margin), Math.max(margin, window.innerWidth - width - margin));
    const gap = isMobile ? 3 : 8;
    this.style.setProperty("--sf-anchor-top", `${Math.round(rect.bottom + gap)}px`);
    this.style.setProperty("--sf-anchor-left", `${Math.round(left)}px`);
    this.style.setProperty("--sf-anchor-width", `${Math.round(width)}px`);
    this.classList.toggle("sf-narrow", width < 700);
  }

  private headers(): HeadersInit {
    return { "content-type": "application/json", "x-api-key": this.config.storeKey };
  }

  private showOutput(): void {
    if (this.root.querySelector<HTMLElement>(".sf-output")!.hidden) this.analytics?.trackOpen();
    this.root.querySelector<HTMLElement>(".sf-output")!.hidden = false;
    if (this.dataset.desktopFullscreen === 'true' && !window.matchMedia('(max-width: 620px), (orientation: landscape) and (max-height: 500px) and (max-width: 1200px)').matches
      && !this.classList.contains('sf-desktop-active')) {
      const input = this.root.querySelector<HTMLInputElement>('input[type="search"]')!;
      this.desktopReturnInput = this.activeInput ?? input;
      input.value = this.desktopReturnInput.value;
      this.activeInput = input;
      this.classList.remove('sf-narrow');
      this.classList.add('sf-desktop-active');
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        if (this.classList.contains('sf-desktop-active')) input.focus({preventScroll:true});
      });
    }
    requestAnimationFrame(() => this.updateScrollHint());
  }

  private hideOutput(): void {
    this.discoveryViewTracked = false;
    this.root.querySelector<HTMLElement>(".sf-output")!.hidden = true;
    if (this.classList.contains('sf-desktop-active')) {
      const input = this.root.querySelector<HTMLInputElement>('input[type="search"]')!;
      if (this.desktopReturnInput) this.desktopReturnInput.value = input.value;
      this.activeInput = this.desktopReturnInput;
      this.classList.remove('sf-desktop-active');
      document.body.style.overflow = this.previousBodyOverflow ?? '';
      this.previousBodyOverflow = undefined;
    }
  }

  private closeSearch(restoreFocus = false): void {
    if (this.debounceTimer) window.clearTimeout(this.debounceTimer);
    window.clearTimeout(this.suggestTimer);
    this.activeRequest?.abort();
    this.suggestRequest?.abort();
    this.hideOutput();
    this.deactivateMobileSearch();
    if (restoreFocus) {
      this.restoringSearchFocus = true;
      (this.externalSearchTrigger ?? this.desktopReturnInput ?? this.activeInput ?? this.root.querySelector<HTMLInputElement>('input[type="search"]'))?.focus({preventScroll:true});
      this.restoringSearchFocus = false;
    }
  }

  /**
   * Sugestii la tastare (modulul „anticipation"). Endpointul răspunde în ~100 ms, mult
   * mai repede decât căutarea completă, deci clientul vede imediat ceva; lista dispare
   * când apar rezultatele, ca să nu se bată cu ele.
   */
  private scheduleSuggestions(input: HTMLInputElement, query: string): void {
    window.clearTimeout(this.suggestTimer);
    if (!this.flags.enabled("anticipation")) {
      this.hideSuggestions();
      return;
    }
    this.suggestTimer = window.setTimeout(() => void this.loadSuggestions(input, query), SUGGEST_DEBOUNCE_MS);
  }

  private async loadSuggestions(input: HTMLInputElement, query: string): Promise<void> {
    this.suggestRequest?.abort();
    this.suggestRequest = new AbortController();
    try {
      const response = await fetch(`${this.config.apiUrl}/suggestions`, {
        method: "POST",
        headers: this.headers(),
        signal: this.suggestRequest.signal,
        body: JSON.stringify({ query, language: this.config.locale, limit: 5 })
      });
      if (!response.ok) throw new Error(`SUGGEST_${response.status}`);
      const data = await response.json() as SuggestionsResponse;
      // Între timp se poate tasta altceva: nu afișăm sugestii pentru o interogare veche.
      if (input.value.trim() !== query) return;
      this.renderSuggestions(input, data.suggestions ?? []);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      this.hideSuggestions();
    }
  }

  private renderSuggestions(input: HTMLInputElement, suggestions: string[]): void {
    const container = this.root.querySelector<HTMLElement>(".sf-suggest");
    const list = this.root.querySelector<HTMLElement>(".sf-suggest-list");
    if (!container || !list) return;
    const items = suggestions.map((item) => String(item).trim()).filter(Boolean).slice(0, 5);
    if (!items.length) {
      this.hideSuggestions();
      return;
    }
    list.replaceChildren(...items.map((text) => {
      const entry = document.createElement("li");
      const button = document.createElement("button");
      button.type = "button";
      button.textContent = this.decodeEntities(text);
      // Fără asta, click-ul scoate focusul din input înainte să apuce să pornească căutarea.
      button.addEventListener("mousedown", (event) => event.preventDefault());
      button.addEventListener("click", () => {
        input.value = text;
        this.hideSuggestions();
        this.showOutput();
        void this.search(text, true);
      });
      entry.append(button);
      return entry;
    }));
    container.hidden = false;
    this.updateScrollHint();
  }

  private hideSuggestions(): void {
    const container = this.root.querySelector<HTMLElement>(".sf-suggest");
    if (container) container.hidden = true;
  }

  /**
   * Panoul se derulează singur când rezultatele depășesc înălțimea ecranului.
   * Fără un indiciu, clientul nu știe că mai are ceva dedesubt (linkul „Vezi toate
   * rezultatele pe site" rămânea ascuns sub margine), așa că afișăm o umbră cât
   * timp mai e conținut și o scoatem când s-a ajuns la capăt.
   */
  private readonly updateScrollHint = (): void => {
    const output = this.root.querySelector<HTMLElement>(".sf-output");
    if (!output) return;
    const more = output.scrollHeight > output.clientHeight + 4;
    const atEnd = output.scrollTop + output.clientHeight >= output.scrollHeight - 4;
    output.classList.toggle("sf-has-more", more && !atEnd);
    output.classList.toggle("sf-at-end", !more || atEnd);
  };

  /**
   * Pe mobil panoul se comportă ca o foaie: se trage în jos de mâner și se închide.
   * Gestul pornește doar când conținutul e derulat sus, ca să nu se bată cu derularea.
   */
  private setupPanelGestures(): void {
    const output = this.root.querySelector<HTMLElement>(".sf-output");
    if (!output) return;
    output.addEventListener("scroll", this.onPanelScroll, { passive: true });
    this.panelObserver?.disconnect();
    this.panelObserver = new MutationObserver(() => this.updateScrollHint());
    this.panelObserver.observe(output, { childList: true, subtree: true });
    window.addEventListener("resize", this.updateScrollHint);
    this.root.querySelector<HTMLButtonElement>(".sf-more")?.addEventListener("click", () => void this.loadMoreResults());
    // Degetul care derulează nu trebuie să și deschidă un produs: măsurăm cât s-a
    // mișcat și blocăm click-ul care ar urma după derulare.
    const results = this.root.querySelector<HTMLElement>(".sf-results");
    if (results) {
      results.addEventListener("touchstart", this.onTouchGuardStart, { passive: true, capture: true });
      results.addEventListener("touchmove", this.onTouchGuardMove, { passive: true, capture: true });
      results.addEventListener("click", this.onClickGuard, { capture: true });
    }
    const grab = this.root.querySelector<HTMLElement>(".sf-grab");
    if (!grab) return;
    grab.addEventListener("touchstart", this.onDragStart, { passive: true });
    grab.addEventListener("touchmove", this.onDragMove, { passive: false });
    grab.addEventListener("touchend", this.onDragEnd);
    grab.addEventListener("touchcancel", this.onDragEnd);
  }

  /**
   * Pe măsură ce clientul derulează în jos, următoarele produse se încarcă singure;
   * butonul „Vezi mai multe produse" rămâne pentru cine preferă să apese.
   */
  private readonly onPanelScroll = (): void => {
    if (this.panelScrollFrame) return;
    this.panelScrollFrame = window.requestAnimationFrame(() => {
      this.panelScrollFrame = 0;
      const output = this.root.querySelector<HTMLElement>(".sf-output");
      if (!output || output.hidden) return;
      const remaining = output.scrollHeight - output.scrollTop - output.clientHeight;
      const runway = Math.max(700, output.clientHeight * 1.5);
      this.updateScrollHint();
      if (remaining < runway) void this.loadMoreResults();
    });
  };

  /**
   * Pe ecran tactil, degetul care derulează lista poate „apăsa” din greșeală un produs,
   * fiindcă tot rândul este clicabil. Reținem de unde a pornit degetul, iar dacă s-a
   * mișcat mai mult decât pragul, blocăm click-ul care ar urma.
   */
  private readonly onTouchGuardStart = (event: TouchEvent): void => {
    const touch = event.touches[0];
    if (!touch) return;
    this.touchOrigin = { x: touch.clientX, y: touch.clientY };
  };

  private readonly onTouchGuardMove = (event: TouchEvent): void => {
    const touch = event.touches[0];
    if (!touch || !this.touchOrigin) return;
    const distance = Math.hypot(touch.clientX - this.touchOrigin.x, touch.clientY - this.touchOrigin.y);
    if (distance > 12) this.suppressClickUntil = Date.now() + 450;
  };

  private readonly onClickGuard = (event: Event): void => {
    if (Date.now() >= this.suppressClickUntil) return;
    event.preventDefault();
    event.stopPropagation();
  };

  private readonly onDragStart = (event: TouchEvent): void => {
    const output = this.root.querySelector<HTMLElement>(".sf-output");
    const touch = event.touches[0];
    if (!output || !touch || !this.classList.contains("sf-mobile-active") || output.scrollTop > 0) return;
    this.dragStartY = touch.clientY;
    this.dragging = true;
    output.style.setProperty("--sf-drag", "0px");
  };

  private readonly onDragMove = (event: TouchEvent): void => {
    if (!this.dragging) return;
    const output = this.root.querySelector<HTMLElement>(".sf-output");
    const touch = event.touches[0];
    if (!output || !touch) return;
    if (output.scrollTop > 0) {
      this.onDragEnd();
      return;
    }
    const distance = Math.max(0, touch.clientY - this.dragStartY);
    if (distance > 4) {
      event.preventDefault();
      output.classList.add("sf-dragging");
      output.style.setProperty("--sf-drag", `${Math.round(distance)}px`);
    }
  };

  private readonly onDragEnd = (): void => {
    if (!this.dragging) return;
    this.dragging = false;
    const output = this.root.querySelector<HTMLElement>(".sf-output");
    if (!output) return;
    const distance = Number.parseFloat(output.style.getPropertyValue("--sf-drag") || "0");
    output.classList.remove("sf-dragging");
    output.style.setProperty("--sf-drag", "0px");
    if (distance > 80) {
      this.hideOutput();
      this.deactivateMobileSearch();
    }
  };

  /** Navigare rapidă prin rezultate și recomandări, inclusiv din inputul magazinului. */
  private focusPanelItem(direction: 1 | -1, current?: HTMLElement): boolean {
    const output = this.root.querySelector<HTMLElement>(".sf-output");
    if (!output || output.hidden) return false;
    const items = Array.from(
      output.querySelectorAll<HTMLElement>("button:not([disabled]), a[href]"),
    ).filter((item) => item.getClientRects().length > 0);
    if (!items.length) return false;
    const index = current ? items.indexOf(current) : -1;
    const next = index < 0
      ? (direction === 1 ? items[0] : items[items.length - 1])
      : items[(index + direction + items.length) % items.length];
    next?.focus();
    return Boolean(next);
  }

  private async search(query: string, preview = false, retried = false): Promise<void> {
    this.outfitPanel?.clear();
    this.searchEpoch += 1;
    this.moreRequest?.abort();
    this.moreRequest = undefined;
    this.loadingMore = false;
    this.resultQuery = "";
    this.visualSearchActive = false;
    const copy = copyFor(this.config.locale);
    const status = this.root.querySelector<HTMLElement>(".sf-search-status")!;
    const results = this.root.querySelector<HTMLElement>(".sf-results")!;
    const normalizedQuery = query.trim();
    if (!normalizedQuery) {
      status.textContent = "";
      this.showDiscover();
      return;
    }
    this.activeRequest?.abort();
    this.activeRequest = new AbortController();
    this.hideDiscover();
    status.textContent = copy.loading;
    this.showOutput();
    this.dispatchEvent(new CustomEvent("smartyfind:search", {
      detail: { query: normalizedQuery, ...this.config },
      // Evenimentul urcă în pagină, ca orice unealtă a magazinului (GTM, pixel,
      // script propriu) să poată reacționa fără să citească din shadow DOM.
      bubbles: true,
      composed: true
    }));
    try {
      const response = await fetch(`${this.config.apiUrl}/search`, {
        method: "POST",
        headers: this.headers(),
        signal: this.activeRequest.signal,
        body: JSON.stringify({ query: normalizedQuery, language: this.config.locale, limit: this.config.maxResults, sessionId: this.sessionId })
      });
      if (!response.ok) throw new Error(`SEARCH_${response.status}`);
      const data = await response.json() as SearchResponse;
      status.textContent = data.results.length
        ? `${data.totalResults} ${data.totalResults===1&&this.config.locale==='ro'?'produs potrivit':copy.results} · ${data.processingTime} ms${data.correctedQuery ? ` · ${copy.corrected} ${data.correctedQuery}` : ""}`
        : copy.noResults;
      this.activeQueryId = data.queryId;
      this.renderUnderstanding(normalizedQuery, data);
      this.resultQuery = normalizedQuery;
      this.refinementSync?.(normalizedQuery);
      this.resultLimit = this.config.maxResults;
      this.resultCount = data.results.length;
      this.renderResults(data.results, data.queryId);
      this.renderSeeAll(normalizedQuery);
      void this.renderContent(normalizedQuery);
      void this.renderSearchBanner(normalizedQuery);
      // Căutarea ajunge și în analytics-ul magazinului (GA4 / Meta / TikTok), dacă există.
      {
        this.analytics?.trackSearch({
          query: normalizedQuery,
          results: data.totalResults,
          locale: this.config.locale
        });
      }
      // Fara rezultate, panoul nu ramane gol: aratam categoriile care se potrivesc
      // interogarii, cautarile populare si recomandarile, ca vizitatorul sa aiba unde
      // merge mai departe.
      if (!data.results.length && this.flags.enabled("search.zero-result")) {
        void this.renderRescue(normalizedQuery);
      }
      this.hideDiscover();
      this.showOutput();
      void Promise.all(data.results.map((product, index) => this.track("PRODUCT_IMPRESSION", {
        queryId: data.queryId,
        productId: product.productId,
        position: index + 1
      })));
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      // O eroare de rețea trecătoare nu trebuie să strice căutarea: reîncercăm o dată.
      if (!retried) {
        await new Promise((resolve) => window.setTimeout(resolve, 450));
        return this.search(normalizedQuery, preview, true);
      }
      status.textContent = copy.unavailable;
      // Keep previous products and offer the store search instead of an empty panel.
      this.renderUnderstanding("", null);
      this.renderSeeAll(normalizedQuery);
      void this.renderContent(normalizedQuery);
      void this.renderSearchBanner(normalizedQuery);
      
      this.showOutput();
    }
  }

  /**
   * Căutare după poză: poza se micșorează în browser (altfel un cadru de telefon are
   * 4-8 MB), apoi API-ul o citește cu AI și întoarce produsele care seamănă.
   */
  private async runVisualSearch(fileInput: HTMLInputElement): Promise<void> {
    const file = fileInput.files?.[0];
    fileInput.value = "";
    if (!file) return;
    await this.runVisualSearchFile(file);
  }

  /** Căutarea pornită de o poză: aleasă dintr-un fișier sau lipită din clipboard. */
  private async runVisualSearchFile(file: File): Promise<void> {
    this.outfitPanel?.clear();
    const epoch = ++this.searchEpoch;
    this.visualSearchActive = true;
    this.root.querySelector('.sf-content-results')?.remove();
    this.root.querySelector('.sf-search-banner')?.remove();
    if (this.debounceTimer) window.clearTimeout(this.debounceTimer);
    this.activeRequest?.abort();
    this.moreRequest?.abort();
    this.panelObserver?.disconnect();
    if (this.panelScrollFrame) window.cancelAnimationFrame(this.panelScrollFrame);
    window.removeEventListener("resize", this.updateScrollHint);
    this.discoverRequest?.abort();
    const copy = copyFor(this.config.locale);
    const status = this.root.querySelector<HTMLElement>(".sf-search-status")!;
    this.hideDiscover();
    this.hideSuggestions();
    this.showOutput();
    status.textContent = copy.visualLoading;
    this.root.querySelector<HTMLElement>(".sf-visual-advice")?.setAttribute("hidden", "");
    // Poza se arată imediat, dintr-un URL local, fără să așteptăm micșorarea din canvas:
    // scanerul trebuie să apară în aceeași clipă în care omul a ales poza, altfel
    // așteptarea de 4-5 secunde pare că nu s-a întâmplat nimic.
    const preview = URL.createObjectURL(file);
    this.renderScanner(preview);
    // 503 vine de la API când furnizorul AI nu poate analiza poza de felul lui (cheie
    // fără cotă, serviciu picat). Nu e o problemă cu poza vizitatorului, deci nu-i
    // spunem să încerce altă poză — l-am trimite să caute o vină care nu e a lui.
    let providerDown = false;
    try {
      const imageUrl = await this.prepareImage(file);
      if (epoch !== this.searchEpoch || !this.visualSearchActive) return;
      if (!imageUrl) throw new Error("VISUAL_IMAGE_UNSUPPORTED");
      const thumbnail = (await this.prepareImage(file, 280, 65536)) ?? undefined;
      if (epoch !== this.searchEpoch || !this.visualSearchActive) return;
      this.activeRequest?.abort();
      this.activeRequest = new AbortController();
      const response = await fetch(`${this.config.apiUrl}/visual-search`, {
        method: "POST",
        headers: this.headers(),
        signal: this.activeRequest.signal,
        body: JSON.stringify({
          imageUrl,
          thumbnail,
          sessionId: this.sessionId,
          size: (this.activeInput?.value ?? this.resultQuery).match(/(?:m[aă]rime(?:a)?\s*|\b)(\d{2}(?:[.,]\d)?)\s*(?:EU\b|$)/i)?.[1],
          language: this.config.locale,
          limit: this.config.maxResults
        })
      });
      if (epoch !== this.searchEpoch || !this.visualSearchActive) return;
      if (!response.ok) {
        providerDown = response.status === 503;
        throw new Error(`VISUAL_${response.status}`);
      }
      const data = (await response.json()) as VisualSearchResponse;
      if (epoch !== this.searchEpoch || !this.visualSearchActive) return;
      this.hideDiscover();
      this.showOutput();
      const detected = [
        ...(data.detectedAttributes?.productType ?? []),
        ...(data.detectedAttributes?.color ?? []),
        ...(data.detectedAttributes?.style ?? [])
      ]
        .map((value) => String(value).trim())
        .filter(Boolean)
        .slice(0, 4);
      status.textContent = data.results.length
        ? `${data.totalResults} ${data.totalResults===1&&this.config.locale==='ro'?'produs potrivit':copy.results} · ${data.processingTime} ms`
        : copy.visualNoResults;
      this.activeQueryId = data.queryId;
      // Rezultatele vin din poză, nu dintr-un text tastat: „mai multe” nu are ce căuta.
      this.resultQuery = "";
      this.resultLimit = this.config.maxResults;
      this.resultCount = data.results.length;
      this.renderResults(data.results, data.queryId);
      if (data.explanation) {
        const note = this.root.querySelector<HTMLElement>(".sf-visual-advice");
        if (note) { note.textContent = data.explanation; note.hidden = false; note.style.whiteSpace = "normal"; note.style.padding = "12px"; note.style.lineHeight = "1.5"; }
      }
      const seeAll = this.root.querySelector<HTMLAnchorElement>(".sf-see-all");
      if (seeAll) {
        seeAll.hidden = true;
        seeAll.removeAttribute("href");
      }
      this.updateResultsFooter();
      this.renderDetected(data.results.length ? detected : []);
      let privacy = this.root.querySelector<HTMLElement>('.sf-visual-privacy');
      if (!privacy) {
        privacy = document.createElement('p'); privacy.className = 'sf-visual-privacy';
        privacy.style.cssText = 'padding:8px 12px;margin:0;font-size:11px;color:#64748b';
        this.root.querySelector('.sf-visual-advice')?.after(privacy);
      }
      privacy.textContent = this.config.locale === 'ro' ? 'Miniatura pozei și rezultatele sunt vizibile magazinului. Miniatura se păstrează 30 de zile.' : 'The store can view the thumbnail and results. The thumbnail is kept for 30 days.';
      if (!data.results.length) { this.showVisualEmptyState(detected); const note = this.root.querySelector<HTMLElement>(".sf-visual-empty"); if (note && data.explanation) note.textContent = data.explanation; }
      if (data.results.length) {
        // Ce a înțeles AI-ul din poză ajunge în analytics ca termen căutat: așa se
        // vede în GA4/Pixel ce produse caută oamenii cu telefonul.
        this.analytics?.trackSearch({
          query: detected.join(" ") || copy.visualAction,
          results: data.totalResults,
          locale: this.config.locale
        });
      }
      void this.track("SUGGESTION_VIEW", {
        metadata: {
          surface: "visual",
          detected: detected.join(" · "),
          results: data.totalResults
        }
      });
    } catch (error) {
      if (epoch !== this.searchEpoch || !this.visualSearchActive) return;
      if (error instanceof DOMException && error.name === "AbortError") return;
      status.textContent = providerDown ? copy.visualUnavailable : copy.visualError;
      this.removeScanner();
      this.renderDetected([]);
    } finally {
      URL.revokeObjectURL(preview);
    }
  }

  /**
   * Scanerul din panou: poza trimisă, cu o bară care trece peste ea cât timp AI-ul o
   * citește. Înainte se vedea doar textul „Citesc poza…", iar cele 4-5 secunde păreau
   * blocate. Arătând poza, omul vede ce se analizează și că lucrul chiar se întâmplă.
   *
   * Se randează în lista de rezultate, fiindcă `renderResults` o golește la sosirea
   * produselor: scanerul dispare singur, fără stări de ascuns de întreținut.
   */
  private renderScanner(previewUrl: string): void {
    const copy = copyFor(this.config.locale);
    const container = this.root.querySelector<HTMLElement>(".sf-results");
    if (!container) return;
    container.replaceChildren();
    const scan = document.createElement("div");
    scan.className = "sf-scan";
    const frame = document.createElement("div");
    frame.className = "sf-scan-frame";
    const photo = document.createElement("img");
    photo.className = "sf-scan-photo";
    photo.alt = "";
    photo.decoding = "async";
    photo.src = previewUrl;
    // A doua copie a pozei, mai luminoasă, se dezvăluie pe măsură ce bara coboară:
    // partea de deasupra barei apare „citită", restul rămâne stins.
    const reveal = document.createElement("img");
    reveal.className = "sf-scan-reveal";
    reveal.alt = "";
    reveal.decoding = "async";
    reveal.src = previewUrl;
    frame.append(photo, reveal);
    for (const layer of ["mesh", "bar"]) {
      const element = document.createElement("span");
      element.className = `sf-scan-${layer}`;
      frame.append(element);
    }
    for (const corner of ["tl", "tr", "bl", "br"]) {
      const bracket = document.createElement("span");
      bracket.className = `sf-scan-corner sf-${corner}`;
      frame.append(bracket);
    }
    const label = document.createElement("p");
    label.className = "sf-scan-label";
    const text = document.createElement("span");
    text.textContent = copy.visualLoading;
    const dots = document.createElement("span");
    dots.className = "sf-scan-dots";
    for (let index = 0; index < 3; index += 1) dots.append(document.createElement("i"));
    label.append(text, dots);
    scan.append(frame, label);
    container.append(scan);
  }

  /** Scoaterea scanerului când poza nu a putut fi citită. */
  private removeScanner(): void {
    this.root.querySelector(".sf-scan")?.remove();
  }

  /** Ce a recunoscut AI-ul în poză, scris deasupra rezultatelor. */
  private renderDetected(values: string[]): void {
    this.root.querySelector(".sf-visual-privacy")?.remove();
    const copy = copyFor(this.config.locale);
    const container = this.root.querySelector<HTMLElement>(".sf-understood")!;
    if (!values.length) {
      container.textContent = "";
      container.style.display = "none";
      return;
    }
    container.replaceChildren();
    const label = document.createElement("strong");
    label.textContent = `${copy.visualDetected} `;
    container.append(label, document.createTextNode(values.join(" · ")));
    container.style.display = "block";
  }

  /**
   * Poza nu a găsit nimic în catalog. Un panou gol nu ajută pe nimeni: arătăm ce
   * are magazinul (categorii, căutări populare, recomandări). Ce am înțeles din poză
   * stă pe rândul lui, ca la rezultate, iar aici rămâne doar îndemnul: vestea proastă
   * se spune o singură dată, în bara de status.
   */
  private showVisualEmptyState(detected: string[]): void {
    const copy = copyFor(this.config.locale);
    this.showDiscover();
    this.renderDetected(detected);
    const note = this.root.querySelector<HTMLElement>(".sf-visual-empty");
    if (!note) return;
    note.textContent = copy.rescueTitle;
    note.hidden = false;
  }

  /**
   * Poza se micșorează în browser înainte de trimitere: 1024 px pe latura lungă, JPEG,
   * sub limita de 2 MB a API-ului. Fără asta, o poză de telefon nu ar încăpea.
   */
  private async prepareImage(file: File, maxSide = 1024, maxChars = 1_400_000): Promise<string | null> {
    if (!file.type.startsWith("image/")) return null;
    const objectUrl = URL.createObjectURL(file);
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error("VISUAL_IMAGE_LOAD"));
        element.src = objectUrl;
      });
      const scale = Math.min(1, maxSide / Math.max(image.naturalWidth, image.naturalHeight));
      const width = Math.max(1, Math.round(image.naturalWidth * scale));
      const height = Math.max(1, Math.round(image.naturalHeight * scale));
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) return null;
      context.drawImage(image, 0, 0, width, height);
      for (const quality of [0.72, 0.55, 0.4]) {
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        if (dataUrl.length <= maxChars) return dataUrl;
      }
      return null;
    } catch {
      return null;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private renderUnderstanding(query: string, data: SearchResponse | null): void {
    this.root.querySelector<HTMLElement>(".sf-visual-advice")?.setAttribute("hidden", "");
    this.root.querySelector(".sf-visual-privacy")?.remove();
    const copy = copyFor(this.config.locale);
    const container = this.root.querySelector<HTMLElement>(".sf-understood")!;
    if (!query || !data?.results.length) {
      container.textContent = "";
      container.style.display = "none";
      return;
    }
    // Filtrele se citesc ca lumea: „Mărimea 43", „În stoc", nu „43 · true".
    const labels: string[] = [];
    for (const [key, raw] of Object.entries(data.appliedFilters ?? {})) {
      const values = Array.isArray(raw) ? raw : [raw];
      if (key === "size") labels.push(`${copy.sizeLabel} ${values.map(String).join(", ")}`);
      else if (key === "inStock") {
        if (raw === true) labels.push(copy.inStockLabel);
      } else labels.push(values.map(String).filter(Boolean).join(", "));
    }
    const relevantLabels=labels.filter(value=>value&&!query.toLocaleLowerCase().includes(value.toLocaleLowerCase()));
    const kept = relevantLabels.slice(0, 3);
    const rest = relevantLabels.length - kept.length;
    // Categoria din feed poate fi un cod („187"): nu are ce căuta în textul
    // pe care îl citește vizitatorul.
    const rawCategory = data.results[0]?.category;
    const firstCategory =
      rawCategory && /[a-z]/i.test(this.decodeEntities(rawCategory))
        ? this.decodeEntities(rawCategory)
        : undefined;
    const understood = [
      ...new Set(
        [query, ...kept, rest > 0 ? `${copy.filtersMoreLabel} (${rest})` : ""].filter(Boolean),
      ),
    ].slice(0, 5);
    container.replaceChildren();
    const label = document.createElement("strong");
    label.textContent = `${copy.understood}: `;
    container.append(label, document.createTextNode(understood.join(" · ")));
    container.style.display = "block";
  }

  /**
   * Chip-urile se derulează pe orizontală, dar nimic nu arăta asta: ultimul chip
   * apărea tăiat la mijlocul cuvântului. Clasa `sf-chip-fade` pune o estompare pe
   * marginea din dreapta cât timp mai există chip-uri în afara ecranului.
   *
   * Observatoarele se țin într-o hartă, câte unul pe rând: nivelul 1 și nivelul 2 se
   * urmăresc amândouă, iar un observator unic, reconectat, ar lăsa primul rând fără
   * estompare de îndată ce se deschide al doilea.
   */
  private watchChipOverflow(chips: HTMLElement): void {
    const update = (): void => {
      const more = chips.scrollWidth - chips.clientWidth - chips.scrollLeft > 4;
      chips.classList.toggle("sf-chip-fade", more);
    };
    const previous = this.chipScrollListeners.get(chips);
    if (previous) chips.removeEventListener("scroll", previous);
    chips.addEventListener("scroll", update, { passive: true });
    this.chipScrollListeners.set(chips, update);
    if (typeof ResizeObserver !== "undefined") {
      // Rândul se schimbă la fiecare randare, deci observatorul vechi se înlocuiește.
      this.chipObservers.get(chips)?.disconnect();
      const observer = new ResizeObserver(update);
      this.chipObservers.set(chips, observer);
      observer.observe(chips);
    }
    requestAnimationFrame(update);
  }

  /** La deconectarea elementului, observatoarele rămân altfel agățate de noduri moarte. */
  private disconnectChipObservers(): void {
    for (const observer of this.chipObservers.values()) observer.disconnect();
    this.chipObservers.clear();
    for (const [element, listener] of this.chipScrollListeners) {
      element.removeEventListener("scroll", listener);
    }
    this.chipScrollListeners.clear();
  }

  /**
   * Ce vede vizitatorul cand cautarea nu intoarce nimic: categoriile care se potrivesc
   * interogarii, cautarile populare si recomandarile, in loc de un panou gol. Vestea
   * proasta s-a spus o singura data, in bara de status; blocul acesta spune doar ce are
   * magazinul. Modulul "search.zero-result" decide daca se foloseste.
   */
  private async renderRescue(query: string): Promise<void> {
    if(this.resultQuery!==query)return;
    try {
      const response=await fetch(`${this.config.apiUrl}/search/refinements`,{
        method:'POST',headers:this.headers(),signal:this.activeRequest?.signal,
        body:JSON.stringify({query,language:this.config.locale,limit:100,page:1,inStock:true,debug:false})
      });
      if(!response.ok||this.resultQuery!==query)return;
      const guidance=await response.json() as {message:string;heading:string;options:Array<{label:string;query:string}>};
      if(this.resultQuery!==query)return;
      const status=this.root.querySelector<HTMLElement>('.sf-search-status');
      if(status)status.textContent=guidance.message;
      if(!guidance.options?.length)return;
      const block=document.createElement('section');block.className='sf-rescue sf-zero-guidance';
      block.append(this.rescueLabel(guidance.heading));
      const choices=document.createElement('div');choices.className='sf-rescue-terms';
      for(const option of guidance.options){
        const button=document.createElement('button');button.type='button';button.className='sf-chip';
        button.textContent=option.label;button.title=option.query;
        button.addEventListener('click',()=>this.pickQuery(option.query,'zero-result:color'));
        choices.append(button);
      }
      block.append(choices);this.root.querySelector<HTMLElement>('.sf-results')!.append(block);
    }catch{ /* Search stays empty; never replace unavailable suggestions with random products. */ }
  }
  private rescueLabel(text: string): HTMLElement {
    const label = document.createElement("p");
    label.className = "sf-rescue-title";
    label.textContent = text;
    return label;
  }

  /** Un rând de chip-uri în blocul de recuperare: fiecare pornește o căutare nouă. */
  private rescueTerms(terms: string[], source: string): HTMLElement {
    const row = document.createElement("div");
    row.className = "sf-rescue-terms";
    for (const term of terms) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "sf-chip";
      button.textContent = term;
      button.addEventListener("click", () => this.pickQuery(term, source));
      row.append(button);
    }
    return row;
  }

  /** Aceeași categorie nu are ce căuta de două ori în rândul de sugestii. */
  private uniqueCategories(entries: FacetValue[]): FacetValue[] {
    const seen = new Set<string>();
    const result: FacetValue[] = [];
    for (const entry of entries) {
      const value = (entry?.value ?? "").trim();
      if (!value || seen.has(value.toLowerCase())) continue;
      seen.add(value.toLowerCase());
      result.push({ value, count: entry.count });
    }
    return result;
  }

  private campaignBadgeLabel:string|null=null;
  private async renderSearchBanner(query:string):Promise<void>{
    this.root.querySelector('.sf-search-banner')?.remove();
    try{const r=await fetch(`${this.config.apiUrl}/recommendations/flow/banner`,{headers:this.headers()});if(!r.ok)return;const {banner,campaignBadge}=await r.json();if(this.resultQuery!==query)return;this.campaignBadgeLabel=campaignBadge==='Black Friday'?campaignBadge:null;refreshCampaignBadges(this.root,this.campaignBadgeLabel);if(!banner||this.visualSearchActive)return;
      const box=document.createElement('aside');box.className='sf-search-banner';box.style.cssText='padding:18px 20px;margin:12px 0;border:1px solid #e2d9f5;border-radius:14px;background:linear-gradient(120deg,#f7f1ff,#f0fbfb);color:#38405c';
      const title=document.createElement('strong');title.textContent=banner.title;const text=document.createElement('p');text.textContent=banner.text;text.style.cssText='font-size:13px;margin:7px 0';box.append(title,text);
      if(banner.url){const u=new URL(banner.url);if(u.protocol==='https:'){const a=document.createElement('a');a.href=u.href;a.textContent=this.config.locale==='ro'?'Descoperă oferta →':'Explore offer →';a.style.cssText='color:#5c45a7;font-weight:600;font-size:13px';box.append(a)}}this.root.querySelector('.sf-results')?.before(box);
    }catch{/* Optional promotions do not delay product search. */}
  }

  private async renderContent(query: string): Promise<void> {
    this.root.querySelector('.sf-content-results')?.remove();
    if (!this.flags.loaded || !this.flags.enabled('search.content')) return;
    try {
      const response = await fetch(`${this.config.apiUrl}/content-search`, {method:'POST',headers:this.headers(),body:JSON.stringify({query})});
      if (!response.ok) return;
      const data = await response.json() as {results:Array<{title:string;url:string;excerpt:string;type:string}>};
      if (this.resultQuery !== query || !data.results?.length || this.visualSearchActive) return;
      const section=document.createElement('section');section.className='sf-content-results';section.style.cssText='padding:16px;margin:12px 0;background:#f8f9ff;border:1px solid #e5e7f0;border-radius:14px';
      const title=document.createElement('h3');title.textContent=this.config.locale==='ro'?'Pagini și articole utile':'Useful pages and articles';title.style.cssText='margin:0 0 12px;font-size:15px';section.append(title);
      for(const item of data.results){const url=new URL(item.url);if(url.protocol!=='https:')continue;const link=document.createElement('a');link.href=url.href;link.textContent=item.title;link.style.cssText='display:block;font-weight:600;color:#5142a1;margin-top:12px';const excerpt=document.createElement('p');excerpt.textContent=item.excerpt;excerpt.style.cssText='font-size:13px;line-height:1.5;margin:5px 0;color:#647089';section.append(link,excerpt);}
      (this.root.querySelector('.sf-outfit')??this.root.querySelector('.sf-results'))?.after(section);
    } catch { /* Product search remains usable when the content index is unavailable. */ }
  }

  private renderResults(products: SearchResult[], queryId: string, append = false): void {
    if(!append){
      this.outfitPanel??=new OutfitPanel({root:this.root,apiUrl:this.config.apiUrl,storeKey:this.config.storeKey,locale:this.config.locale,
        headers:()=>this.headers(),track:(type,detail)=>this.track(type,detail),
        onProduct:(product,id)=>this.rememberAttribution(id,product.productId,{productName:product.name,productUrl:product.productUrl,imageUrl:product.imageUrl})});
      const anchor=products[0];if(anchor)this.outfitPanel.show(anchor,queryId);else this.outfitPanel.clear();
    }
    this.hideSuggestions();
    const copy = copyFor(this.config.locale);
    const container = this.root.querySelector<HTMLElement>(".sf-results")!;
    if (!append) { container.replaceChildren(); this.root.querySelector<HTMLElement>(".sf-output")!.scrollTop = 0; }
    const listed = this.uniqueByProduct(products);
    const wanted = new Set(listed.map(product => product.productId));
    if (append) for (const row of Array.from(container.querySelectorAll<HTMLElement>(".sf-product"))) {
      if (!wanted.has(row.dataset.sfProductId ?? "")) row.remove();
    }
    const existing = new Set(Array.from(container.querySelectorAll<HTMLElement>(".sf-product")).map(row => row.dataset.sfProductId));
    const fragment = document.createDocumentFragment();
    let added = 0;
    let similarHeadingAdded=Boolean(container.querySelector('.sf-visual-alternatives-title'));
    // O linie per produs: serverul pliază deja familiile, dar un duplicat nu are ce
    // căuta în listă nici dacă vine din altă sursă (recomandări, „mai multe produse").
    this.sidebarResults = listed;
    this.sidebarQueryId = queryId;
    if(this.resultQuery||this.visualSearchActive){const sidebar=this.root.querySelector<HTMLElement>('.sf-editorial-reco');if(sidebar){sidebar.hidden=true;sidebar.replaceChildren();}}
    // Main results always stay in the grid. FVE's editorial carousel is separate.
    if(!append && listed.length && this.flags.enabled('discover.panel')) void this.loadDiscover();
    for (const [index, product] of listed.entries()) {
      if (existing.has(product.productId)) continue;
      if(product.matchTier==='alternative'&&!similarHeadingAdded){
        const title=document.createElement('h3');title.className='sf-visual-alternatives-title';
        title.textContent=this.config.locale==='ro'?'Modele asemănătoare':'Similar styles';
        title.style.cssText='grid-column:1/-1;margin:14px 0 3px;font-size:15px;line-height:1.5;color:#263047';
        fragment.append(title);similarHeadingAdded=true;
      }
      const position = existing.size + added + 1;
      const row = document.createElement("article");
      row.className = "sf-product";
      row.dataset.sfProductId = product.productId;
      const imageWrap = document.createElement("a");
      imageWrap.className = "sf-image";
      const image = document.createElement("img");
      image.alt = this.decodeEntities(product.name);
      image.loading = added < 8 ? "eager" : "lazy";
      image.decoding = "async";
      if (product.imageUrl) image.src = product.imageUrl;
      imageWrap.append(image);
      markCampaignOffer(imageWrap,this.config.showPrices&&this.isOnSale(product.price,product.salePrice),this.campaignBadgeLabel);
      if (this.config.showPrices && this.isOnSale(product.price, product.salePrice)) {
        const percent = Math.round((1 - product.salePrice! / product.price!) * 100);
        if (Number.isFinite(percent) && percent > 0 && percent < 100) {
          const discount = document.createElement("span");
          discount.className = "sf-result-discount";
          discount.textContent = `-${percent}%`;
          imageWrap.append(discount);
        }
      }
      if (product.aiVisual) {
        const imageBadge = document.createElement("span");
        imageBadge.className = "sf-image-badge";
        imageBadge.textContent = copy.visualMatch;
        imageWrap.append(imageBadge);
      }
      const productCopy = document.createElement("div");
      productCopy.className = "sf-product-copy";
      const name = document.createElement("a");
      name.textContent = this.decodeEntities(product.name);
      name.href = this.decodeEntities(product.productUrl ?? "#");
      name.dataset.sfProductId = product.productId;
      name.dataset.sfPosition = String(position);
      name.dataset.sfQueryId = queryId;
      name.dataset.sfProductName = this.decodeEntities(product.name);
      name.dataset.sfImageUrl = product.imageUrl ?? "";
      const onProductClick = () => {
        if (this.navigating) return;
        void this.track("PRODUCT_CLICK", {
          queryId,
          productId: product.productId,
          position: Number(name.dataset.sfPosition),
          metadata: { surface: "search-results" }
        });
      };
      name.addEventListener("click",onProductClick);
      imageWrap.href=name.href;
      Object.assign(imageWrap.dataset,name.dataset);
      imageWrap.addEventListener("click",onProductClick);
      const meta = document.createElement("div");
      meta.className = "sf-meta";
      meta.textContent = [product.category, product.brand, product.color?`${this.config.locale==='ro'?'Culoare':'Colour'}: ${product.color}`:'']
        .filter((value): value is string => Boolean(value))
        .map((value) => this.decodeEntities(value))
        .join(" · ") || copy.aiLabel;
      const tags = document.createElement("div");
      tags.className = "sf-tags";
      const visualTags = product.aiVisual
        ? Object.values(product.aiVisual.attributes).flat().slice(0, 3)
        : [];
      for (const value of visualTags) {
        const tag = document.createElement("span");
        tag.className = "sf-tag";
        tag.textContent = value;
        tags.append(tag);
      }
      productCopy.append(name, meta);
      if (this.config.locale === "ro" && product.matchReasons?.length) {
        const reason = document.createElement("p");
        reason.style.fontSize = "12px";
        reason.style.lineHeight = "1.5";
        reason.textContent = [...product.matchReasons.slice(0, 2), ...(product.differences ?? []).slice(0, 2)].join(" · ");
        productCopy.append(reason);
      }
      if (product.availableSizes?.length) {
        const sizes = document.createElement('div'); sizes.className='sf-sizes';
        const label=document.createElement('span'); label.className='sf-sizes-label';label.textContent=copy.availableSizes;sizes.append(label);
        for(const value of product.availableSizes){const size=document.createElement('span');size.className='sf-size';size.textContent=value;sizes.append(size);}
        productCopy.append(sizes);
      }
      if (visualTags.length) productCopy.append(tags);
      const badge = productBadge(product, this.config.locale, { showPrices: this.config.showPrices });
      if (badge) {
        const reason = document.createElement("span");
        reason.className = `sf-result-reason is-${badge.kind}`;
        reason.textContent = badge.text;
      if(badge.title)reason.title=badge.title;
        productCopy.append(reason);
      }
      row.append(imageWrap, productCopy);
      if (this.config.showPrices) {
        const price = document.createElement("div");
        price.className = "sf-price";
        if (this.isOnSale(product.price, product.salePrice)) {
          const old = document.createElement("span");
          old.className = "sf-old";
          old.textContent = this.money(product.price, product.currency);
          price.append(old);
        }
        price.append(document.createTextNode(this.money(product.salePrice ?? product.price, product.currency)));

        row.append(price);
      }
      fragment.append(row);
      added += 1;
    }
    container.append(fragment);
    const visibleProducts=container.querySelectorAll('.sf-product').length;
    container.classList.toggle('sf-few-results',!this.visualSearchActive&&visibleProducts>0&&visibleProducts<=2);
    Array.from(container.querySelectorAll<HTMLElement>(".sf-product")).forEach((row, index) => {
      for (const link of Array.from(row.querySelectorAll<HTMLElement>("[data-sf-position]"))) link.dataset.sfPosition = String(index + 1);
    });
  }

  /**
   * Link către pagina de rezultate a magazinului. Panoul rămâne o sugestie:
   * cine vrea lista completă, cu filtrele site-ului, are o cale directă acolo.
   */
  private siteSearchUrl(query: string): string | null {
    // Fullscreen uses an internal form; use the original storefront search form.
    const input = this.desktopReturnInput ?? this.activeInput;
    const form = input && !this.root.contains(input) ? input.form : this.hijackInputs.find((item) => item.form)?.form;
    if (!form) return null;
    const fields: Array<[string, unknown]> = [];
    new FormData(form).forEach((value, key) => fields.push([key, value]));
    return buildSiteSearchUrl(form.getAttribute("action"), window.location.href, query, fields);
  }

  private renderSeeAll(query: string): void {
    const link = this.root.querySelector<HTMLAnchorElement>(".sf-see-all")!;
    const copy = copyFor(this.config.locale);
    const url = this.siteSearchUrl(query);
    link.hidden = !url;
    if (!url) {
      this.updateResultsFooter();
      return;
    }
    link.href = url;
    link.textContent = this.resultCount ? copy.seeAll : this.config.locale==='ro'?'Caută în magazin':'Search the store';
    link.onclick = () => {
      void this.track("SUGGESTION_CLICK", { metadata: { surface: "see-all", query } });
    };
    this.updateResultsFooter();
  }

  /** Mai sunt rezultate de arătat dacă ultima cerere a fost plină și nu s-a atins plafonul. */
  private hasMoreResults(): boolean {
    return (
      Boolean(this.resultQuery) &&
      this.resultLimit > 0 &&
      this.resultCount >= this.resultLimit &&
      this.resultLimit < this.maxResultLimit
    );
  }

  /** Bara de jos se ascunde doar când nu mai există nicio cale de continuare. */
  private updateResultsFooter(): void {
    const copy = copyFor(this.config.locale);
    const foot = this.root.querySelector<HTMLElement>(".sf-foot");
    const more = this.root.querySelector<HTMLButtonElement>(".sf-more");
    const seeAll = this.root.querySelector<HTMLAnchorElement>(".sf-see-all");
    if (!foot || !more || !seeAll) return;
    more.hidden = !this.hasMoreResults();
    more.disabled = this.loadingMore;
    // Pe mobil lista se încarcă la derulare, deci butonul devine un simplu indiciu.
    const onMobile = window.matchMedia("(max-width: 620px), (orientation: landscape) and (max-height: 500px) and (max-width: 1200px)").matches;
    more.textContent = this.loadingMore ? copy.loadingMore : onMobile ? copy.scrollMore : copy.loadMore;
    foot.hidden = more.hidden && seeAll.hidden;
  }

  private async prepareResultImages(products: SearchResult[]): Promise<void> {
    let timer: number | undefined;
    const ready = Promise.all(products.slice(0, 8).map(async product => {
      if (!product.imageUrl) return;
      const image = new Image();
      image.decoding = "async";
      image.src = product.imageUrl;
      await image.decode().catch(() => {});
    }));
    try {
      await Promise.race([ready, new Promise<void>(resolve => { timer = window.setTimeout(resolve, 350); })]);
    } finally { if (timer !== undefined) window.clearTimeout(timer); }
  }

  private async loadMoreResults(): Promise<void> {
    if (this.loadingMore || !this.hasMoreResults()) return;
    const query = this.resultQuery;
    const epoch = this.searchEpoch;
    const controller = new AbortController();
    this.moreRequest = controller;
    const previousIds = new Set(this.sidebarResults.map(product => product.productId));
    const previous = this.resultCount;
    const nextLimit = Math.min(this.maxResultLimit, this.resultLimit + this.config.maxResults);
    this.loadingMore = true;
    this.updateResultsFooter();
    try {
      const response = await fetch(`${this.config.apiUrl}/search`, {
        method: "POST",
        headers: this.headers(),
        signal: controller.signal,
        body: JSON.stringify({
          query,
          language: this.config.locale,
          limit: nextLimit,
          sessionId: this.sessionId
        })
      });
      if (!response.ok) throw new Error(`SEARCH_${response.status}`);
      const data = await response.json() as SearchResponse;
      if (epoch !== this.searchEpoch || query !== this.resultQuery || this.visualSearchActive) return;
      await this.prepareResultImages(data.results.filter(product => !previousIds.has(product.productId)));
      if (epoch !== this.searchEpoch || query !== this.resultQuery || this.visualSearchActive) return;
      this.resultLimit = nextLimit;
      this.resultCount = data.results.length;
      this.activeQueryId = data.queryId;
      this.renderResults(data.results, data.queryId, true);
      // Contorul din capul panoului urmează lista, altfel pare că nu s-a încărcat nimic.
      const status = this.root.querySelector<HTMLElement>('.sf-search-status');
      if (status) status.textContent = `${data.results.length} ${copyFor(this.config.locale).results} · ${data.processingTime} ms`;
      void Promise.all(
        data.results.filter(product => !previousIds.has(product.productId)).map((product, index) => this.track("PRODUCT_IMPRESSION", {
          queryId: data.queryId,
          productId: product.productId,
          position: previous + index + 1
        }))
      );
    } catch {
      // Rețea căzută: butonul rămâne, clientul poate reîncerca.
    } finally {
      if (this.moreRequest === controller) {
        this.moreRequest = undefined;
        this.loadingMore = false;
        this.updateResultsFooter();
      }
    }
  }

  private money(value: number | null, currency: string): string {    if (value === null) return "";
    return new Intl.NumberFormat(this.config.locale, { style: "currency", currency }).format(value);
  }

  private isOnSale(price: number | null, salePrice: number | null): boolean {
    return price !== null && salePrice !== null && salePrice > 0 && salePrice < price;
  }

  private isAvailable(value?: string): boolean {
    if (!value) return false;
    return !/out[\s_-]*of[\s_-]*stock|indisponibil|unavailable|sold[\s_-]*out/i.test(value);
  }

  private usefulLabel(value?: string | null): string | undefined {
    if (!value) return undefined;
    const decoded = this.decodeEntities(value).trim();
    if (!decoded || /^\d+$/.test(decoded) || /^pantofi[-\s]?noi(?:\.ro)?$/i.test(decoded)) return undefined;
    return decoded;
  }

  private decodeEntities(value: string): string {
    const textarea = document.createElement("textarea");
    let decoded = value;
    for (let pass = 0; pass < 3; pass += 1) {
      textarea.innerHTML = decoded;
      if (textarea.value === decoded) break;
      decoded = textarea.value;
    }
    return decoded.replace(/&amp;/gi, "&").replace(/&#0*38;/gi, "&");
  }

  /**
   * Produsul deschis de vizitator, venit ca atribut pe tag-ul widgetului
   * (`data-product-view`). Se trimite o singura data per produs si sesiune.
   */
  private reportProductView(): void {
    const raw = this.dataset.productView;
    if (!raw) return;
    let detail: Record<string, unknown>;
    try {
      detail = JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return;
    }
    const name = typeof detail.name === "string" ? detail.name : "";
    if (!name) return;
    this.rememberAttribution(undefined, undefined, {
      productName: name,
      productUrl: typeof detail.url === "string" ? detail.url : window.location.href,
      imageUrl: typeof detail.imageUrl === "string" ? detail.imageUrl : undefined
    });
    const guard = `smartyfind_view_${name}`;
    try {
      if (sessionStorage.getItem(guard)) return;
      sessionStorage.setItem(guard, "1");
    } catch {
      // Fara sessionStorage trimitem oricum.
    }
    delete this.dataset.productView;
    void this.track("PRODUCT_IMPRESSION", {
      metadata: {
        surface: "product-page",
        name,
        sku: typeof detail.sku === "string" ? detail.sku : undefined
      }
    });
  }

  /**
   * Comanda venita de la magazin, fie ca atribut pe tag-ul widgetului
   * (`data-purchase`), fie ca variabila globala pusa de site. Se trimite o
   * singura data per comanda si sesiune de browser, ca o reincarcare a paginii
   * de multumire sa nu dubleze conversia.
   */
  private sendStorePurchase(): void {
    const fromAttribute = this.dataset.purchase;
    const fromWindow = (window as unknown as { smartyfindPendingPurchase?: unknown })
      .smartyfindPendingPurchase;
    const raw = fromAttribute ?? fromWindow;
    if (!raw) return;

    let detail: Record<string, unknown>;
    if (typeof raw === "string") {
      try {
        detail = JSON.parse(raw) as Record<string, unknown>;
      } catch {
        return;
      }
    } else if (typeof raw === "object") {
      detail = raw as Record<string, unknown>;
    } else {
      return;
    }

    if (String(detail.type ?? "") !== "PURCHASE") return;
    this.clearCartRecovery();
    const orderId = typeof detail.orderId === "string" ? detail.orderId : undefined;
    if (orderId) {
      const guard = `smartyfind_purchase_${orderId}`;
      try {
        if (sessionStorage.getItem(guard)) return;
        sessionStorage.setItem(guard, "1");
      } catch {
        // Fara sessionStorage (mod privat) trimitem oricum; mai bine dublu decat deloc.
      }
    }

    delete this.dataset.purchase;
    delete (window as unknown as { smartyfindPendingPurchase?: unknown })
      .smartyfindPendingPurchase;

    void this.track("PURCHASE", {
      orderId,
      value: typeof detail.value === "number" ? detail.value : undefined,
      currency: typeof detail.currency === "string" ? detail.currency : undefined,
      quantity: typeof detail.quantity === "number" ? detail.quantity : undefined,
      queryId: typeof detail.queryId === "string" ? detail.queryId : this.activeQueryId,
      metadata: orderId ? { orderReference: orderId } : undefined
    });
  }

  private sendStoreCheckout(): void {
    if (this.dataset.checkout !== "1") return;
    try {
      if (sessionStorage.getItem("smartyfind_checkout_sent")) return;
      sessionStorage.setItem("smartyfind_checkout_sent", "1");
    } catch {
      // In mod privat evenimentul poate fi retrimis; ingestia ramane idempotenta per eventId.
    }
    delete this.dataset.checkout;
    this.snoozeCartRecovery();
    void this.track("CHECKOUT_START");
  }

  private getSessionId(): string {
    const key = "smartyfind_session";
    try {
      const existing = JSON.parse(localStorage.getItem(key) ?? "null") as { id?: string; createdAt?: number } | null;
      if (existing?.id && existing.createdAt && Date.now() - existing.createdAt < 30 * 86_400_000) return existing.id;
      const created = crypto.randomUUID();
      localStorage.setItem(key, JSON.stringify({ id: created, createdAt: Date.now() }));
      return created;
    } catch {
      return crypto.randomUUID();
    }
  }

  private rememberAttribution(
    queryId?: string,
    productId?: string,
    context: Partial<Pick<AttributionState, "productName" | "productUrl" | "imageUrl" | "assistantConversationId">> = {}
  ): void {
    try {
      const existing = JSON.parse(localStorage.getItem("smartyfind_attribution") ?? "null") as AttributionState | null;
      localStorage.setItem("smartyfind_attribution", JSON.stringify({
        ...existing,
        ...context,
        queryId: queryId ?? existing?.queryId,
        productId: productId ?? existing?.productId,
        at: Date.now()
      } satisfies AttributionState));
    } catch {
      // Atribuirea nu trebuie sa blocheze navigarea.
    }
  }

  private currentAttribution(): AttributionState | null {
    try {
      const value = JSON.parse(localStorage.getItem("smartyfind_attribution") ?? "null") as AttributionState | null;
      if (!value?.at || Date.now() - value.at > 7 * 86_400_000) return null;
      return value;
    } catch {
      return null;
    }
  }

  private readCartRecovery(): CartRecoveryState | null {
    try {
      const value = JSON.parse(localStorage.getItem("smartyfind_cart_recovery") ?? "null") as CartRecoveryState | null;
      if (value?.schemaVersion !== 7 || !value.addedAt || Date.now() - value.addedAt > 7 * 86_400_000) {
        localStorage.removeItem("smartyfind_cart_recovery");
        return this.cartRecoveryState;
      }
      this.cartRecoveryState = value;
      return value;
    } catch {
      return this.cartRecoveryState;
    }
  }

  private writeCartRecovery(value: CartRecoveryState): void {
    this.cartRecoveryState = value;
    try {
      localStorage.setItem("smartyfind_cart_recovery", JSON.stringify(value));
    } catch {
      // Recuperarea coșului rămâne opțională în browserele fără stocare locală.
    }
  }

  /** Coșurile create înainte de încărcarea trackingului primesc totuși recuperare. */
  private seedStoreCart(): void {
    const raw = this.dataset.cartSeed;
    if (!raw) return;
    try {
      const seed = JSON.parse(raw) as Record<string, unknown>;
      const now = Date.now();
      const previous = this.readCartRecovery();
      const regularPrice = Number(seed.regularPrice);
      const salePrice = Number(seed.salePrice);
      this.writeCartRecovery({
        ...previous,
        schemaVersion: 7,
        addedAt: previous?.addedAt ?? now,
        at: now,
        quantity: Math.max(1, Number(seed.quantity) || 1),
        productName: previous?.productName ?? (typeof seed.productName === "string" ? seed.productName : undefined),
        productUrl: previous?.productUrl ?? (typeof seed.productUrl === "string" ? seed.productUrl : undefined),
        imageUrl: previous?.imageUrl ?? (typeof seed.imageUrl === "string" ? seed.imageUrl : undefined),
        regularPrice: previous?.regularPrice ?? (Number.isFinite(regularPrice) && regularPrice > 0 ? regularPrice : undefined),
        salePrice: previous?.salePrice ?? (Number.isFinite(salePrice) && salePrice > 0 ? salePrice : undefined),
        currency: previous?.currency ?? (typeof seed.currency === "string" ? seed.currency : undefined),
        shownCount: previous?.shownCount ?? 0
      });
    } catch {
      // Un atribut incomplet nu trebuie sa afecteze cautarea.
    }
  }

  private rememberCart(detail: Record<string, unknown>): void {
    const previous = this.readCartRecovery();
    const attribution = this.currentAttribution();
    const metadata = detail.metadata && typeof detail.metadata === "object"
      ? detail.metadata as Record<string, unknown>
      : {};
    const now = Date.now();
    const regularPrice = Number(metadata.regularPrice);
    const salePrice = Number(metadata.salePrice);
    this.writeCartRecovery({
      ...attribution,
      schemaVersion: 7,
      addedAt: now,
      at: now,
      quantity: typeof metadata.cartQuantity === "number" && Number.isFinite(metadata.cartQuantity) && metadata.cartQuantity > 0
        ? metadata.cartQuantity
        : (previous?.quantity ?? 0) + Math.max(1, Number(detail.quantity) || 1),
      productName: typeof metadata.productName === "string" ? metadata.productName : attribution?.productName,
      productUrl: typeof metadata.productUrl === "string" ? metadata.productUrl : attribution?.productUrl,
      imageUrl: typeof metadata.imageUrl === "string" ? metadata.imageUrl : attribution?.imageUrl,
      regularPrice: Number.isFinite(regularPrice) && regularPrice > 0 ? regularPrice : previous?.regularPrice,
      salePrice: Number.isFinite(salePrice) && salePrice > 0 ? salePrice : previous?.salePrice,
      currency: typeof metadata.currency === "string" ? metadata.currency : previous?.currency,
      // O adaugare noua reprezinta o intentie noua si trebuie sa poata porni
      // din nou recuperarea, chiar daca un popup anterior a fost deja afisat.
      shownCount: 0,
      lastShownAt: undefined,
      dismissedUntil: undefined,
      snoozedUntil: undefined
    });
    this.scheduleCartRecovery(this.cartRecoveryOffer.delayMinutes * 60_000 + 250);
  }

  private readonly onCartRecoveryReturn = (): void => {
    if (document.visibilityState === "hidden") return;
    this.maybeShowCartRecovery();
  };

  private forgetCart(detail: Record<string, unknown>): void {
    const previous = this.readCartRecovery();
    if (!previous) return;
    const metadata = detail.metadata && typeof detail.metadata === "object" ? detail.metadata as Record<string, unknown> : {};
    const remaining = typeof metadata.cartQuantity === "number" && Number.isFinite(metadata.cartQuantity)
      ? Math.max(0, metadata.cartQuantity)
      : Math.max(0, (previous.quantity ?? 0) - Math.max(1, Number(detail.quantity) || 1));
    if (!remaining) { this.clearCartRecovery(); return; }
    const seed = metadata.cartSeed && typeof metadata.cartSeed === "object" ? metadata.cartSeed as Record<string, unknown> : {};
    this.writeCartRecovery({
      ...previous, quantity: remaining, at: Date.now(),
      productName: typeof seed.productName === "string" ? seed.productName : previous.productName,
      productUrl: typeof seed.productUrl === "string" ? seed.productUrl : previous.productUrl,
      imageUrl: typeof seed.imageUrl === "string" ? seed.imageUrl : previous.imageUrl,
      regularPrice: typeof seed.regularPrice === "number" ? seed.regularPrice : previous.regularPrice,
      salePrice: typeof seed.salePrice === "number" ? seed.salePrice : previous.salePrice,
      currency: typeof seed.currency === "string" ? seed.currency : previous.currency,
    });
    this.root?.querySelector<HTMLElement>(".sf-cart-recovery")?.setAttribute("hidden", "");
  }

  private clearCartRecovery(): void {
    this.cartRecoveryState = null;
    try {
      localStorage.removeItem("smartyfind_cart_recovery");
    } catch {
      // Fără stocare locală nu există popup persistent de curățat.
    }
    this.root?.querySelector<HTMLElement>(".sf-cart-recovery")?.setAttribute("hidden", "");
  }

  private snoozeCartRecovery(): void {
    const state = this.readCartRecovery();
    if (!state) return;
    this.writeCartRecovery({ ...state, snoozedUntil: Date.now() + 2 * 60 * 60_000 });
  }

  private dismissCartRecovery(): void {
    const popup = this.root.querySelector<HTMLElement>(".sf-cart-recovery");
    if (popup) popup.hidden = true;
    const state = this.readCartRecovery();
    if (state) this.writeCartRecovery({ ...state, dismissedUntil: Date.now() + 24 * 60 * 60_000 });
  }

  private scheduleCartRecovery(delayMs: number): void {
    if (this.cartRecoveryTimer) window.clearTimeout(this.cartRecoveryTimer);
    this.cartRecoveryTimer = window.setTimeout(() => {
      this.cartRecoveryTimer = undefined;
      this.maybeShowCartRecovery();
    }, Math.max(250, delayMs));
  }

  private maybeShowCartRecovery(): void {
    if (!this.root) return;
    if (document.visibilityState !== "visible") return;
    if (/\/(cos|cart|checkout|finalizare-comanda|order-received)(\/|$)/i.test(window.location.pathname)) return;
    if (!this.cartRecoveryOfferLoaded) {
      this.scheduleCartRecovery(500);
      return;
    }
    if (!this.cartRecoveryOffer.enabled) return;
    const state = this.readCartRecovery();
    const now = Date.now();
    if (!state) return;
    const remainingDelay = this.cartRecoveryOffer.delayMinutes * 60_000 - (now - state.addedAt);
    if (remainingDelay > 0) {
      // Navigarea distruge temporizatorul paginii anterioare. Reprogramam aici
      // restul intervalului, altfel recuperarea nu mai apare in fluxul normal.
      this.scheduleCartRecovery(remainingDelay + 250);
      return;
    }
    if ((state.dismissedUntil ?? 0) > now || (state.snoozedUntil ?? 0) > now) return;
    if ((state.lastShownAt ?? 0) > now - 24 * 60 * 60_000 || (state.shownCount ?? 0) >= 3) return;

    if (this.cartRecoveryValidationPending) return;
    this.cartRecoveryValidationPending = true;
    void this.verifyCartRecovery(state).then(verified => {
      if (verified) this.showCartRecoveryPopup(verified);
    }).finally(() => { this.cartRecoveryValidationPending = false; });
  }

  private async verifyCartRecovery(expected: CartRecoveryState): Promise<CartRecoveryState | null> {
    const endpoint = this.dataset.cartStatusUrl;
    if (!endpoint) return expected;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 3000);
    try {
      const url = new URL(endpoint, window.location.href);
      if (url.origin !== window.location.origin) return null;
      url.searchParams.set('_sf_cart', String(Date.now()));
      const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!response.ok) return null;
      const data = await response.json() as { success?: boolean; data?: { quantity?: number; seed?: Record<string, unknown> } };
      if (!data.success || typeof data.data?.quantity !== 'number' || !Number.isFinite(data.data.quantity)) return null;
      const current = this.readCartRecovery();
      if (!current || current.at !== expected.at) return null;
      if (data.data.quantity <= 0) { this.clearCartRecovery(); return null; }
      const seed = data.data.seed;
      if (!seed || typeof seed.productName !== 'string' || typeof seed.productUrl !== 'string') return null;
      const verified: CartRecoveryState = { ...current, quantity: data.data.quantity,
        productName: seed.productName, productUrl: seed.productUrl,
        imageUrl: typeof seed.imageUrl === 'string' ? seed.imageUrl : undefined,
        regularPrice: typeof seed.regularPrice === 'number' ? seed.regularPrice : undefined,
        salePrice: typeof seed.salePrice === 'number' ? seed.salePrice : undefined,
        currency: typeof seed.currency === 'string' ? seed.currency : current.currency };
      this.writeCartRecovery(verified);
      return verified;
    } catch { return null; }
    finally { window.clearTimeout(timeout); }
  }

  private showCartRecoveryPopup(state: CartRecoveryState): void {
    const now = Date.now();
    if (!this.isConnected || document.visibilityState !== 'visible' || !this.cartRecoveryOffer.enabled) return;
    if (/\/(cos|cart|checkout|finalizare-comanda|order-received)(\/|$)/i.test(window.location.pathname)) return;
    if ((state.dismissedUntil ?? 0) > now || (state.snoozedUntil ?? 0) > now || (state.lastShownAt ?? 0) > now - 24 * 60 * 60_000) return;

    const popup = this.root.querySelector<HTMLElement>(".sf-cart-recovery");
    if (!popup) return;
    const title = popup.querySelector<HTMLElement>(".sf-cart-title")!;
    const message = popup.querySelector<HTMLElement>(".sf-cart-message")!;
    title.textContent = this.cartRecoveryOffer.headline;
    message.textContent = this.cartRecoveryOffer.message;
    const product = popup.querySelector<HTMLElement>(".sf-cart-copy small")!;
    product.hidden = !state.productName;
    product.textContent = state.productName ?? "";
    const media = popup.querySelector<HTMLElement>(".sf-cart-media")!;
    const image = media.querySelector<HTMLImageElement>("img")!;
    media.hidden = !state.imageUrl;
    popup.classList.toggle("sf-cart-no-media", !state.imageUrl);
    image.src = state.imageUrl ?? "";
    image.alt = state.productName ?? "Produs din coș";
    image.onerror = () => {
      media.hidden = true;
      popup.classList.add("sf-cart-no-media");
    };
    const pricing = popup.querySelector<HTMLElement>(".sf-cart-pricing")!;
    const oldPrice = pricing.querySelector<HTMLElement>("s")!;
    const salePrice = pricing.querySelector<HTMLElement>("b")!;
    const discount = pricing.querySelector<HTMLElement>(".sf-cart-discount")!;
    const regularValue = state.regularPrice ?? 0;
    const saleValue = state.salePrice ?? regularValue;
    const hasProductDiscount = regularValue > saleValue && saleValue > 0;
    pricing.hidden = saleValue <= 0;
    oldPrice.hidden = !hasProductDiscount;
    oldPrice.textContent = hasProductDiscount ? this.money(regularValue, state.currency ?? "RON") : "";
    salePrice.textContent = saleValue > 0 ? this.money(saleValue, state.currency ?? "RON") : "";
    discount.hidden = !hasProductDiscount;
    discount.textContent = hasProductDiscount ? `-${Math.round((1 - saleValue / regularValue) * 100)}%` : "";

    const coupon = popup.querySelector<HTMLElement>(".sf-cart-coupon")!;
    const couponActive = this.cartRecoveryOffer.couponEnabled && !!this.cartRecoveryOffer.couponCode && !!this.cartRecoveryOffer.couponPercent;
    coupon.hidden = !couponActive;
    coupon.querySelector<HTMLElement>("b")!.textContent = couponActive ? `Cod ${this.cartRecoveryOffer.couponCode}` : "";
    coupon.querySelector<HTMLElement>("span")!.textContent = couponActive
      ? `Încă -${this.cartRecoveryOffer.couponPercent}%${this.cartRecoveryOffer.minimumSubtotal ? ` la coșuri de minimum ${this.money(this.cartRecoveryOffer.minimumSubtotal, state.currency ?? "RON")}` : ""}`
      : "";
    const action = popup.querySelector<HTMLAnchorElement>(".sf-cart-action")!;
    const actionUrl = new URL(this.config.cartUrl, window.location.href);
    if (couponActive) actionUrl.searchParams.set("sf_coupon", this.cartRecoveryOffer.couponCode!);
    action.href = actionUrl.toString();
    popup.hidden = false;
    this.writeCartRecovery({ ...state, lastShownAt: now, shownCount: (state.shownCount ?? 0) + 1 });
    void this.track("SUGGESTION_VIEW", { metadata: {
      surface: "cart-recovery",
      productName: state.productName,
      productUrl: state.productUrl,
      quantity: state.quantity,
      regularPrice: state.regularPrice,
      salePrice: state.salePrice,
      couponCode: couponActive ? this.cartRecoveryOffer.couponCode : undefined,
      couponPercent: couponActive ? this.cartRecoveryOffer.couponPercent : undefined
    } });
  }

  private async track(type: string, detail: Record<string, unknown> = {}): Promise<void> {
    if (new URL(location.href).searchParams.has("sf_verify")) return;
    if (type === "PRODUCT_CLICK" && typeof detail.productId === "string") {
      this.analytics?.trackProduct({ productId: detail.productId,
        productName: typeof detail.productName === "string" ? detail.productName : undefined,
        query: this.resultQuery, position: typeof detail.position === "number" ? detail.position : undefined });
    }
    const enriched = { ...detail };
    if (["ADD_TO_CART", "CHECKOUT_START", "PURCHASE"].includes(type)) {
      const clickId=currentRecommendationClick(this.config.storeKey);
      if(clickId)enriched.metadata={...(enriched.metadata as Record<string,unknown>??{}),recommendationClickEventId:clickId};
    }
    if (["ADD_TO_CART", "CHECKOUT_START", "PURCHASE"].includes(type)) {
      const attribution = this.currentAttribution();
      if (typeof enriched.queryId !== "string" && attribution?.queryId) enriched.queryId = attribution.queryId;
      if (typeof enriched.productId !== "string" && attribution?.productId) enriched.productId = attribution.productId;
    }
    const assistant = ['ADD_TO_CART','CHECKOUT_START','PURCHASE'].includes(type) ? this.currentAttribution()?.assistantConversationId : (enriched.metadata as Record<string,unknown>|undefined)?.assistantConversationId;
    if(assistant){ const meta: Record<string,unknown>={...(enriched.metadata as Record<string,unknown>??{}),surface:'shopping-assistant',assistantConversationId:assistant};
      const stage=({PRODUCT_CLICK:'product_click',ADD_TO_CART:'add_to_cart',CHECKOUT_START:'checkout',PURCHASE:'purchase_assist'} as Record<string,string>)[type];
      if(stage)meta['gaSent']=this.config.analytics!=='off'&&emitAssistantAnalytics(stage,enriched);
      enriched.metadata=meta;
    }
    const event = {
      type,
      sessionId: this.sessionId,
      language: this.config.locale,
      device: matchMedia("(max-width: 600px)").matches ? "mobile" : matchMedia("(max-width: 1024px)").matches ? "tablet" : "desktop",
      referrer: location.href,
      timestamp: new Date().toISOString(),
      ...enriched,
      eventId: typeof enriched.eventId === "string" ? enriched.eventId : crypto.randomUUID()
    };
    if (["PRODUCT_CLICK", "ADD_TO_CART", "REMOVE_FROM_CART", "CHECKOUT_START", "PURCHASE"].includes(type)) {
      this.commerceDelivery ??= new EventDelivery(`${this.config.apiUrl}/events`, this.headers(), `smartyfind_events_${this.config.storeKey.slice(-16)}`);
      await this.commerceDelivery.send(trackingPayload(event) as Record<string, unknown> & {eventId:string});
      return;
    }
    try {
      await fetch(`${this.config.apiUrl}/events`, {
        method: "POST",
        headers: this.headers(),
        keepalive: true,
        body: JSON.stringify({ events: [trackingPayload(event)] })
      });
    } catch {
      // Tracking must never block or break search.
    }
  }
}

registerCategoryMerchandising();

if (!customElements.get("smartyfind-search")) customElements.define("smartyfind-search", SmartyFindElement);

const script = document.currentScript as HTMLScriptElement | null;
if (script?.dataset.storeKey) {
  const element = document.createElement("smartyfind-search");
  Object.assign(element.dataset, script.dataset);
  const mount = () => {
    let selectorMatches = false;
    try { selectorMatches = !!element.dataset.hijackSelector && !!document.querySelector(element.dataset.hijackSelector); } catch { /* Replace invalid defaults only for a detected platform. */ }
    const profile = storefrontProfile(element.dataset, !!document.querySelector('input.search__input[name="q"][data-search-url]'), selectorMatches);
    for (const [key, value] of Object.entries(profile)) if (value !== undefined) element.dataset[key] = value;
    if (document.head.contains(script) || element.dataset.openSelector) document.body.appendChild(element);
    else script.insertAdjacentElement("afterend", element);
  };
  // Some storefront themes reparent their body during DOMContentLoaded.
  // Mount after that pass, otherwise custom-element initialization runs twice.
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => requestAnimationFrame(mount), { once: true });
  else requestAnimationFrame(mount);
}


